"use client";

// Модуль «Задачи» отдела IT — личная доска задач (канбан).
// Единственная вкладка отдела IT (ai). Источник данных:
//   • TeamTask, назначенные текущему пользователю (api.listTasks("inbox")) —
//     создаются через FAB «Задачи»; раскладываются по столбцам-статусам;
//   • dev_requests со статусом «new» — в столбце «Заявки на доработку»
//     (триаж: «в задачи» = создать TeamTask, или «убрать»).
// Этапы пока фиксированные (new/in_progress/done) — управление этапами позже.

import { Fragment, useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState, type CSSProperties, type ReactNode } from "react";
import { createPortal } from "react-dom";
import { useSearchParams } from "next/navigation";
import { Archive, ArchiveRestore, ArrowRight, CalendarDays, CircleDot, CirclePlus, Download, Eye, Flag, GripVertical, LayoutGrid, ListChecks, MoreHorizontal, Phone, Rows3, Search, Users } from "lucide-react";
import { UserAvatar } from "@/components/ui/user-avatar";
import { IconAttach, IconCheck, IconChevronDown, IconChevronRight, IconClose, IconEdit, IconLink, IconSpinner, IconTrash } from "@/components/ui/icons";

import { api } from "@/lib/api";
import { toast } from "@/lib/toast";
import { useAuth } from "@/components/auth-provider";
import { hasPermission } from "@/lib/access";
import { useKanbanTouchDrag } from "@/lib/use-kanban-touch-drag";
import { deptColorOf } from "@/lib/dept-colors";
import { PRIORITIES, PRIORITY_OPTIONS, prioColor, prioFill, prioLabel, type TaskPriority } from "@/lib/task-priority";
import { TASK_STAGES, stageColor, stageFill, stageLabelOne } from "@/lib/task-status";
import { Select } from "@/components/ui/select";
import { Popover, PopoverAnchor, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Calendar } from "@/components/ui/calendar";
import { DateRangePicker } from "@/components/ui/date-picker";
import { AuthImage } from "@/components/ui/auth-image";
import { authStreamUrl } from "@/lib/auth-blob";
import { ItprCalendarWidget } from "@/components/it-tasks/itpr-calendar-widget";
import { ItprDeptMigrationWidget } from "@/components/it-tasks/itpr-dept-migration-widget";
import { ItprDeptRequestsWidget } from "@/components/it-tasks/itpr-dept-requests-widget";
import { KnowledgeBaseWidget } from "@/components/it-tasks/knowledge-base-widget";
import { TabHeader } from "@/components/ui/tab-header";
import type { Department, DevRequest, DevRequestPriority, TaskEvent, TaskStage, TeamAttachment, TeamTask, TeamTaskStatus } from "@/lib/types";
import { INPUT_CLS, Input, TEXTAREA_CLS } from "@/components/ui/input";
import { Z } from "@/lib/z";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Modal } from "@/components/ui/modal";
import { DataTable, type DataCol } from "@/components/ui/data-table";
import { confirmDialog } from "@/components/ui/confirm";

type Props = {
  embedded?: boolean;
  /** Что показывать. ⚠️ Доска ПЕРЕЕХАЛА В КАБИНЕТ (Максим, 01.09.2026: «зачем
   *  теперь доска задач в отделе IT, если есть в кабинете?» → «всё в кабинет»).
   *  Личные задачи — это личное, а не про отдел IT; в IT остались виджеты,
   *  которые действительно его: заявки на доработку, переезд отделов на портал,
   *  календарь и база знаний. Компонент один, второй копии доски нет. */
  only?: "board" | "widgets";
  initialTab?: string;
  onSubtabChange?: (subKey: string) => void;
  canEdit?: boolean;
  canDelete?: boolean;
};


// ⚠️ Подписи и цвета приоритета — ТОЛЬКО из `lib/task-priority.ts`. Свои
// словари жили в шести файлах и успели разойтись.
const PRIO_OPTIONS = PRIORITY_OPTIONS;
/** Приоритет метками цвета — как в образце: строка списка И ЕСТЬ чип, без
 *  повторения подписи рядом. */
const PRIO_OPTIONS_CHIP = PRIORITIES.map((p) => ({
  value: p.value,
  label: p.label,
  render: (
    <span className="inline-block truncate rounded-[var(--radius-md)] px-1.5 py-px text-[11px] font-semibold"
          style={{ background: prioFill(p.value), color: p.color }}>{p.label}</span>
  ),
}));

// Начальная позиция поповера РЯДОМ с карточкой (справа; слева — если не влезает),
// БЕЗ maxHeight — чтобы layout-эффект замерил натуральную высоту. anchor=null → центр.
function besidePos(anchor: DOMRect | null, width: number): CSSProperties {
  if (!anchor) {
    return { position: "fixed", left: "50%", top: 8, transform: "translateX(-50%)", width, zIndex: Z.MODAL + 1 };
  }
  let left = anchor.right + 8;
  if (left + width > window.innerWidth - 8) left = anchor.left - width - 8;
  if (left < 8) left = Math.min(Math.max(8, anchor.left), window.innerWidth - width - 8);
  return { position: "fixed", left, top: 8, width, zIndex: Z.MODAL + 1 };
}

// ⚠️ ПОРЯДОК КОЛОНОК И ПОДПИСИ СТАТУСОВ — из `lib/task-status.ts`. Здесь их
// было три словаря подряд, и они уже разошлись с кабинетом и профилем.
const STAGES = TASK_STAGES;
const REQ_COLOR = "#06b6d4";

const fmtDate = (iso: string) => (iso ? `${iso.slice(8, 10)}.${iso.slice(5, 7)}` : "");
// Срок на карточке — ОТРЕЗКОМ, как в образце («18 мая — 25 мая»). Одна дата —
// «до 05.09» или «с 05.09». Нет ни одной — не печатаем ничего: строка «без
// срока» стояла на каждой второй карточке и была шумом, а не сведением.
const fmtPeriod = (start: string, due: string) =>
  start && due ? `${fmtDate(start)} — ${fmtDate(due)}` : due ? `до ${fmtDate(due)}` : start ? `с ${fmtDate(start)}` : "";
// ISO <-> локальная Date без сдвига часового пояса (для портального календаря).
const toIsoDate = (d: Date) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
const fromIso = (s: string): Date | undefined => { if (!s) return undefined; const [y, m, d] = s.split("-").map(Number); return new Date(y, (m || 1) - 1, d || 1); };
// Авто-рост textarea под содержимое (для безрамочного заголовка задачи — текст виден целиком).
const autoGrow = (el: HTMLTextAreaElement | null) => { if (!el) return; el.style.height = "auto"; el.style.height = `${el.scrollHeight}px`; };

function ColumnHeader({ label, color, count }: { label: string; color: string; count: number }) {
  return (
    <div className="mb-2 inline-flex items-center gap-1.5 self-start rounded-[var(--radius-md)] px-2.5 py-1"
      style={{ color, background: `color-mix(in srgb, ${color} 14%, transparent)`, border: `1px solid color-mix(in srgb, ${color} 40%, transparent)` }}>
      <span className="h-1.5 w-1.5 rounded-full" style={{ background: color }} />
      <span className="text-[11px] font-bold" >{label}</span>
      <span className="text-[11px] font-semibold opacity-70 tabular-nums">{count}</span>
    </div>
  );
}

// Список референсов: миниатюры картинок / чипы файлов и ссылок + удаление.
/** «1,17 МБ» — размер файла с ЗАПЯТОЙ, как все числа в портале. */
const fmtSize = (b: number): string => {
  if (!b) return "";
  if (b < 1024) return `${b} Б`;
  if (b < 1024 * 1024) return `${Math.round(b / 1024)} КБ`;
  return `${(b / 1024 / 1024).toFixed(2).replace(".", ",")} МБ`;
};
/** Дата и время В МЕСТНОМ поясе. `created_at` приходит из базы без пояса (UTC),
 *  и без пометки «Z» время показывалось на три часа раньше настоящего. */
const fmtLocalDateTime = (iso: string): string => {
  if (!iso) return "";
  const d = new Date(iso.endsWith("Z") || iso.includes("+") ? iso : iso + "Z");
  const p2 = (n: number) => String(n).padStart(2, "0");
  return `${p2(d.getDate())}.${p2(d.getMonth() + 1)}.${d.getFullYear()} ${p2(d.getHours())}:${p2(d.getMinutes())}`;
};
/** «несколько секунд назад» / «12 минут назад» / «вчера» / «14.08.2026». */
const fmtAgo = (iso: string): string => {
  if (!iso) return "";
  const t = new Date(iso.endsWith("Z") || iso.includes("+") ? iso : iso + "Z").getTime();
  const сек = Math.max(0, Math.round((Date.now() - t) / 1000));
  if (сек < 45) return "несколько секунд назад";
  const мин = Math.round(сек / 60);
  if (мин < 60) return `${мин} мин назад`;
  const час = Math.round(мин / 60);
  if (час < 24) return `${час} ч назад`;
  if (час < 48) return "вчера";
  return fmtDateTime(iso).slice(0, 10);
};
// ⚠️ ИСТОРИЯ: фразу собирает ИНТЕРФЕЙС, в базе лежат факты (kind + поле +
// было/стало). Так история переживает переименование полей и читается по-русски.
const ПОЛЕ_РОД: Record<string, string> = {
  status: "статус", title: "название", description: "описание", priority: "приоритет",
  start_date: "срок", due_date: "срок", category: "проект", tags: "теги",
  task_type: "тип", estimate_min: "оценку времени", link: "ссылку",
  assignee_ids: "исполнителей",
};
/** Значение поля человеческим языком: статусы и приоритеты — подписями. */
const значениеПоля = (поле: string, v: string): string => {
  if (!v) return "—";
  if (поле === "status") return STAGES.find((x) => x.key === v)?.label ?? v;
  if (поле === "priority") return prioLabel(v as TaskPriority) || v;
  if (поле === "estimate_min") return fmtEstimate(Number(v) || 0) || "—";
  if (поле === "start_date" || поле === "due_date") return fmtDate(v) || v;
  return v;
};
/** «создал(-а) задачу», «изменил(-а) приоритет» — что именно произошло. */
const фразаСобытия = (e: TaskEvent): string => {
  switch (e.kind) {
    case "created": return "создал(-а) задачу";
    case "archived": return "отправил(-а) в архив";
    case "restored": return "вернул(-а) из архива";
    case "subtask_added": return "добавил(-а) подзадачу";
    case "subtask_done": return "выполнил(-а) подзадачу";
    case "subtask_undone": return "снял(-а) отметку с подзадачи";
    case "subtask_removed": return "удалил(-а) подзадачу";
    case "subtask_renamed": return "переименовал(-а) подзадачу";
    case "file_added": return "добавил(-а) файл";
    case "file_removed": return "удалил(-а) файл";
    case "field":
      if (e.field === "title") return "переименовал(-а) задачу";
      if (e.field === "description") return "изменил(-а) описание";
      return `изменил(-а) ${ПОЛЕ_РОД[e.field] ?? e.field}`;
    default: return e.kind;
  }
};
/** Подробность события строкой под фразой. Пусто — строки нет. */
const подробностьСобытия = (e: TaskEvent): string => {
  if (e.kind === "field") {
    if (e.field === "description") return "";
    if (e.field === "assignee_ids") return "";
    return `${значениеПоля(e.field, e.value_from)} → ${значениеПоля(e.field, e.value_to)}`;
  }
  if (e.kind === "subtask_renamed") return `${e.value_from} → ${e.value_to}`;
  return e.ref_title || "";
};
/** «сегодня, 12:21» · «10 апреля, 11:43» · «10.04.2025, 11:43» — как в образце. */
const МЕСЯЦЫ_РОД = ["января","февраля","марта","апреля","мая","июня","июля","августа","сентября","октября","ноября","декабря"];
const датаСобытия = (iso: string): string => {
  if (!iso) return "";
  const d = new Date(iso.endsWith("Z") || iso.includes("+") ? iso : iso + "Z");
  const p2 = (n: number) => String(n).padStart(2, "0");
  const время = `${p2(d.getHours())}:${p2(d.getMinutes())}`;
  const сег = new Date();
  const тотЖеДень = d.toDateString() === сег.toDateString();
  if (тотЖеДень) return `сегодня, ${время}`;
  const вчера = new Date(сег); вчера.setDate(сег.getDate() - 1);
  if (d.toDateString() === вчера.toDateString()) return `вчера, ${время}`;
  if (d.getFullYear() === сег.getFullYear()) return `${d.getDate()} ${МЕСЯЦЫ_РОД[d.getMonth()]}, ${время}`;
  return `${p2(d.getDate())}.${p2(d.getMonth() + 1)}.${d.getFullYear()}, ${время}`;
};

/** Расширение файла для подписи под плиткой: «png», «pdf». */
const fileExt = (a: TeamAttachment): string => {
  const m = (a.original_name || a.filename || "").match(/\.([a-zA-Z0-9]{1,6})$/);
  return m ? m[1].toLowerCase() : (a.mime_type || "").split("/")[1] || "";
};
const isImage = (a: TeamAttachment) => a.kind === "image" || /^image\//.test(a.mime_type);

// ⚠️ ФАЙЛЫ ЗАДАЧИ — ПО ОБРАЗЦУ WEEEK (разбор их интерфейса 02.09.2026).
// Списком: миниатюра, имя, справа размер и «когда». Плиткой: превью 136 px,
// имя и подпись «1,17 МБ · png». В конце — пунктирная плашка «Файл», чтобы
// приложить следующий, не поднимаясь к шапке раздела. Нажатие открывает
// просмотр: кто и когда приложил, скачать, удалить, закрыть.
function FileList({ items, canEdit, view, onDelete, onEdit, onAdd }: {
  items: TeamAttachment[];
  canEdit: boolean;
  view: "list" | "grid";
  onDelete: (id: number) => void;
  onEdit: (a: TeamAttachment) => void;
  onAdd: () => void;
}) {
  const [просмотр, setПросмотр] = useState<TeamAttachment | null>(null);
  const открыть = (a: TeamAttachment) => {
    if (a.kind === "link") { window.open(a.url, "_blank", "noreferrer"); return; }
    setПросмотр(a);
  };
  // Скачивание — прямой ссылкой с токеном: файлы отдаются под Bearer, обычный
  // href на них отвечает 401.
  const скачать = (a: TeamAttachment) => {
    const el = document.createElement("a");
    el.href = a.kind === "link" ? a.url : authStreamUrl(a.url);
    el.download = a.original_name || a.filename || "файл";
    el.target = "_blank";
    document.body.appendChild(el); el.click(); el.remove();
  };

  // ⚠️ ПОД КУРСОРОМ — «Скачать» ПОДПИСЬЮ и меню «…», как в образце (скрин
  // Максима 02.09.2026). Значками без подписей человек не понимает, что
  // предлагают; у них подписано, и мы так же. Пункта «Подписать» у нас нет —
  // электронной подписи в портале не существует, рисовать её незачем.
  const [меню, setМеню] = useState<number | null>(null);
  // Пункт меню — СТАНДАРТНЫЙ класс портала `.menu-item` (globals.css): та же
  // подсветка под курсором, что у выпадающих списков и таблиц.
  const пункт = "menu-item text-[12px]";
  const действия = (a: TeamAttachment) => (
    <>
      {a.kind !== "link" && (
        <button type="button" onClick={(e) => { e.stopPropagation(); скачать(a); }}
          className="file-act glass-hover glass-flat inline-flex shrink-0 items-center gap-1 rounded-[var(--radius-md)] px-1.5 py-0.5 text-[11px] text-[var(--textMuted)]">
          <Download size={12} /> Скачать
        </button>
      )}
      <Popover open={меню === a.id} onOpenChange={(v) => setМеню(v ? a.id : null)}>
        <PopoverAnchor asChild>
          <button type="button" onClick={(e) => { e.stopPropagation(); setМеню((m) => (m === a.id ? null : a.id)); }}
            className="file-act glass-hover glass-flat shrink-0 rounded-[var(--radius-md)] px-1 text-[var(--textMuted)]" aria-label="Ещё">
            <MoreHorizontal size={14} />
          </button>
        </PopoverAnchor>
        <PopoverContent align="end" className="w-[168px] p-1" onClick={(e) => e.stopPropagation()}>
          <button type="button" className={пункт} onClick={() => { setМеню(null); открыть(a); }}>
            <Eye size={13} className="text-[var(--textMuted)]" /> Посмотреть
          </button>
          {a.kind !== "link" && (
            <button type="button" className={пункт} onClick={() => { setМеню(null); скачать(a); }}>
              <Download size={13} className="text-[var(--textMuted)]" /> Скачать
            </button>
          )}
          {canEdit && (
            <button type="button" className={пункт} onClick={() => { setМеню(null); onEdit(a); }}>
              <IconEdit size={13} className="text-[var(--textMuted)]" /> Переименовать
            </button>
          )}
          {canEdit && (
            <button type="button" className={`${пункт} file-act-danger`} onClick={() => { setМеню(null); onDelete(a.id); }}>
              <IconTrash size={13} className="text-[var(--textMuted)]" /> Удалить
            </button>
          )}
        </PopoverContent>
      </Popover>
    </>
  );

  const значок = (a: TeamAttachment, размер: number) =>
    isImage(a)
      ? <AuthImage src={a.url} alt={a.original_name} className="h-full w-full object-cover" />
      : <span className="grid h-full w-full place-items-center text-[var(--textMuted)]">
          {a.kind === "link" ? <IconLink size={размер} /> : <IconAttach size={размер} />}
        </span>;

  return (
    <div className="mt-1 flex flex-col gap-1.5">
      {view === "list" ? (
        <div className="flex flex-col gap-0.5">
          {items.map((a) => (
            <div key={a.id} role="button" tabIndex={0}
              onClick={() => открыть(a)}
              onKeyDown={(e) => { if (e.target === e.currentTarget && (e.key === "Enter" || e.key === " ")) { e.preventDefault(); открыть(a); } }}
              className="file-row flex cursor-pointer items-center gap-2 rounded-[var(--radius-md)] px-1.5 py-1 text-left">
              <span className="h-5 w-5 shrink-0 overflow-hidden rounded-[4px] border border-[var(--border)] bg-[var(--secondary)]">
                {значок(a, 11)}
              </span>
              <span className="min-w-0 flex-1 truncate text-[12px] text-[var(--text)]">{a.original_name || a.url}</span>
              <span className="file-meta flex shrink-0 items-center gap-3 text-[11px] text-[var(--textMuted)]">
                {a.size_bytes > 0 && <span className="tabular-nums">{fmtSize(a.size_bytes)}</span>}
                <span>{fmtAgo(a.created_at)}</span>
              </span>
              {действия(a)}
            </div>
          ))}
          {canEdit && (
            <button type="button" onClick={onAdd}
              className="file-add glass-flat mt-1 flex items-center justify-center gap-1.5 rounded-[var(--radius-md)] py-2 text-[11px] text-[var(--textMuted)]">
              <IconAttach size={12} /> Файл
            </button>
          )}
        </div>
      ) : (
        <div className="flex flex-wrap gap-2">
          {items.map((a) => (
            <div key={a.id} role="button" tabIndex={0}
              onClick={() => открыть(a)}
              onKeyDown={(e) => { if (e.target === e.currentTarget && (e.key === "Enter" || e.key === " ")) { e.preventDefault(); открыть(a); } }}
              className="file-tile relative w-[132px] cursor-pointer rounded-[var(--radius-md)] p-1">
              <span className="block h-[104px] w-full overflow-hidden rounded-[var(--radius-md)] border border-[var(--border)] bg-[var(--secondary)]">
                {значок(a, 22)}
              </span>
              <span className="mt-1 block truncate text-[12px] text-[var(--text)]">{a.original_name || a.url}</span>
              <span className="block truncate text-[11px] text-[var(--textMuted)]">
                {[fmtSize(a.size_bytes), fileExt(a)].filter(Boolean).join(" · ")}
              </span>
              <span className="absolute right-1.5 top-1.5 flex gap-1">{действия(a)}</span>
            </div>
          ))}
          {canEdit && (
            <button type="button" onClick={onAdd}
              className="file-add glass-flat grid h-[104px] w-[132px] place-items-center rounded-[var(--radius-md)] text-[11px] text-[var(--textMuted)]">
              <span className="flex flex-col items-center gap-1"><IconAttach size={14} /> Файл</span>
            </button>
          )}
        </div>
      )}

      {/* Просмотр файла: кто и когда приложил + скачать и удалить, как у них. */}
      {просмотр && typeof document !== "undefined" && createPortal(
        <div className="fixed inset-0 flex flex-col bg-black/80" style={{ zIndex: Z.LIGHTBOX }}
             onClick={() => setПросмотр(null)}>
          <div className="flex items-center gap-3 px-4 py-3 text-white" onClick={(e) => e.stopPropagation()}>
            <span className="min-w-0">
              {/* ⚠️ Имя берём У САМОГО ФАЙЛА (`uploaded_by_name`), а не у того,
                  кто сейчас смотрит: иначе на чужом файле стояло бы твоё имя. */}
              <span className="block truncate text-[12px] font-semibold">{просмотр.uploaded_by_name || "—"}</span>
              <span className="block text-[11px] opacity-70">{fmtLocalDateTime(просмотр.created_at)}</span>
            </span>
            <span className="flex-1" />
            <button type="button" onClick={() => скачать(просмотр)}
              className="glass-flat rounded-[var(--radius-md)] p-1.5 text-white/80 hover:text-white" data-tooltip="Скачать"><Download size={16} /></button>
            {canEdit && (
              <button type="button" onClick={() => { onDelete(просмотр.id); setПросмотр(null); }}
                className="glass-flat rounded-[var(--radius-md)] p-1.5 text-white/80 hover:text-white" data-tooltip="Удалить"><IconTrash size={16} /></button>
            )}
            <button type="button" onClick={() => setПросмотр(null)}
              className="glass-flat rounded-[var(--radius-md)] p-1.5 text-white/80 hover:text-white" data-tooltip="Закрыть"><IconClose size={16} /></button>
          </div>
          <div className="grid flex-1 place-items-center px-6 pb-6" onClick={() => setПросмотр(null)}>
            {isImage(просмотр)
              ? <AuthImage src={просмотр.url} alt={просмотр.original_name} className="max-h-[82dvh] max-w-[92vw] rounded-[var(--radius-md)] object-contain" />
              : <span className="rounded-[var(--radius-lg)] bg-[var(--surface)] px-6 py-8 text-center text-[12px] text-[var(--text)]">
                  {просмотр.original_name}
                  <span className="mt-2 block text-[11px] text-[var(--textMuted)]">Предпросмотр для этого типа не делаем — нажмите «Скачать».</span>
                </span>}
          </div>
          <div className="px-4 pb-4" onClick={(e) => e.stopPropagation()}>
            <span className="inline-flex max-w-full items-center gap-2 rounded-[var(--radius-md)] bg-[var(--surface)] px-2 py-1 text-[11px] text-[var(--text)]">
              <span className="h-4 w-4 overflow-hidden rounded-[3px] border border-[var(--border)]">{значок(просмотр, 10)}</span>
              <span className="truncate">{просмотр.original_name}</span>
            </span>
          </div>
        </div>, document.body)}
    </div>
  );
}

// Аватар автора задачи: инициалы в цветном кружке (цвет — из имени).
function AuthorAvatar({ name, size = 32 }: { name: string; size?: number }) {
  const initials = (name || "?").split(/\s+/).filter(Boolean).slice(0, 2).map((w) => w[0]).join("").toUpperCase() || "?";
  return (
    <span className="grid shrink-0 place-items-center rounded-full font-semibold text-white"
      style={{ width: size, height: size, fontSize: Math.round(size * 0.4), background: "var(--primary)" }} data-tooltip={name}>{initials}</span>
  );
}
// Дата+время создания: «ДД.ММ.ГГГГ ЧЧ:ММ» из ISO-строки.
const fmtDateTime = (iso: string) =>
  iso ? `${iso.slice(8, 10)}.${iso.slice(5, 7)}.${iso.slice(0, 4)} ${iso.slice(11, 13)}:${iso.slice(14, 16)}` : "";

/** ⚠️ ЗНАЧЕНИЯ ПОЛЕЙ КАРТОЧКИ — ТОЛЬКО СТАНДАРТНЫЕ ЭЛЕМЕНТЫ ПОРТАЛА.
 *  Здесь была своя константа со своим видом (сначала без рамки, потом с
 *  пунктирной), и каждый заход Максим находил новое: «это что — тени или
 *  кнопки?», «как понять, что нажимается?», «не вписывается ни с одним
 *  элементом, потому что в других местах этой тени попросту нет» (01.09.2026).
 *  Причина одна: я рисовал вид заново вместо того, чтобы взять готовый.
 *  Выбор из списка — `Select`, ввод текста — `Input`, дата — `DatePicker`.
 *  Ничего своего: у них уже решены рамка, наведение, фокус и кегль — и человек
 *  узнаёт элемент, потому что видел его на других вкладках. */

/** Строка поля карточки: подпись слева, значение справа — раскладка Weeek.
 *
 *  ⚠️ ЗНАЧЕНИЕ НЕ ТЯНЕТСЯ НА ВСЮ ПАНЕЛЬ. Поля были во всю ширину, и Максим
 *  спросил: «почему они такие широкие?» (01.09.2026). В образце значение —
 *  короткая строка рядом с подписью, а не полоса от края до края: длинная
 *  пустая рамка выглядит как незаполненная форма, хотя это просто «дата не
 *  выбрана». Ширина 240px — под самое длинное реальное значение (дата словами)
 *  и под выпадашки; всё, что длиннее, переносится внутри.  */
function Поле({ подпись, children }: { подпись: string; children: ReactNode }) {
  return (
    <div className="flex items-start gap-3 py-1 text-[12px]">
      <span className="w-[104px] shrink-0 pt-0.5 text-[var(--textMuted)]">{подпись}</span>
      <span className="min-w-0 max-w-[240px] flex-1">{children}</span>
    </div>
  );
}

/** ⚠️ Типы задач — РОВНО те три, что в образце: «Действие», «Встреча», «Звонок».
 *  Раньше я дописал сюда «Веха» и «Идея» от себя, и Максим справедливо спросил,
 *  откуда взялись пункты, непонятные никому (01.09.2026). Список из образца не
 *  расширять: каждый лишний пункт человек вынужден разгадывать.
 *  Пустое значение отдельным пунктом не нужно — его показывает placeholder. */
const TYPE_OPTIONS = [
  { value: "Действие", label: "Действие", icon: <CircleDot size={13} /> },
  { value: "Встреча", label: "Встреча", icon: <Users size={13} /> },
  { value: "Звонок", label: "Звонок", icon: <Phone size={13} /> },
];

// ⚠️ ОЦЕНКА ВРЕМЕНИ — ОКНОМ «Часы / Минуты», как в Weeek (скрин Максима,
// 02.09.2026). Сначала здесь был ввод минут числом: человек должен был сам
// перевести «полтора часа» в 90. Потом — список готовых значений, но он не
// давал произвольную оценку. Окно решает и то, и другое.
// ⚠️ ТЕГИ — ЦВЕТНЫМИ ПЛАШКАМИ и выбором из уже заведённых (образец Weeek,
// скрин Максима 02.09.2026). Было поле «через запятую»: человек печатал теги
// руками и разводил дубли — «wb», «WB», «вб». Свой справочник тегов не заводим:
// список собирается из тегов, которые уже стоят на задачах.
// Палитра — общая палитра портала (та же, что у отделов).
const TAG_PALETTE = ["#8B5CF6", "#EC4899", "#F59E0B", "#10B981", "#0EA5E9", "#6366F1", "#14B8A6", "#F97316"];
/** Цвет тега устойчив: одно и то же слово всегда одного цвета. */
const tagColor = (тег: string) => {
  let h = 0;
  for (let i = 0; i < тег.length; i++) h = (h * 31 + тег.charCodeAt(i)) >>> 0;
  return TAG_PALETTE[h % TAG_PALETTE.length];
};
/** Надпись белая или тёмная — по яркости плашки: на янтарном белым не прочитать. */
const tagInk = (hex: string) => {
  const n = parseInt(hex.slice(1), 16);
  const яркость = 0.299 * ((n >> 16) & 255) + 0.587 * ((n >> 8) & 255) + 0.114 * (n & 255);
  return яркость > 150 ? "#1b1b1f" : "#ffffff";
};
/** Плашка тега: и в поле, и в списке выбора, и при просмотре без прав. */
function TagChip({ тег, onRemove }: { тег: string; onRemove?: () => void }) {
  const bg = tagColor(тег);
  return (
    <span className="inline-flex max-w-full items-center gap-1 rounded-[var(--radius-md)] px-1.5 py-0.5 text-[11px] font-semibold"
          style={{ background: bg, color: tagInk(bg) }}>
      {/* Решётку рисуем САМИ, а из значения снимаем: в старых задачах теги
          заводили строкой «#nhb», и на экране выходило «##nhb». */}
      <span className="truncate">#{тег.replace(/^#+/, "")}</span>
      {onRemove && (
        <button type="button" aria-label={`Убрать тег ${тег}`}
          onClick={(e) => { e.stopPropagation(); onRemove(); }}
          className="glass-flat -mr-0.5 shrink-0 opacity-70 transition hover:opacity-100" style={{ color: "inherit" }}>
          <IconClose size={10} />
        </button>
      )}
    </span>
  );
}

/** «90» → «1 ч 30 мин», «45» → «45 мин». Пусто, когда оценки нет. */
const fmtEstimate = (min: number): string => {
  if (!min) return "";
  const ч = Math.floor(min / 60), м = min % 60;
  if (!ч) return `${м} мин`;
  return м ? `${ч} ч ${м} мин` : `${ч} ч`;
};

// Панель деталей задачи: редактирование задачи + этапы/ячейки (чек-лист) с
// заметкой и референсами на каждую ячейку. Открывается по клику на карточку.
export function TaskDetailPanel({ task, allTasks, anchor = null, canEdit, onClose, onChanged, onOpenTask, onPickParent, onDelete, onArchive, inline = false }: {
  task: TeamTask; allTasks: TeamTask[]; anchor?: DOMRect | null; canEdit: boolean; onClose: () => void; onChanged: () => void; onOpenTask: (t: TeamTask, rect: DOMRect | null) => void;
  /** Перенос задачи в подзадачи: родителя выбирают КЛИКОМ ПО ДОСКЕ, поэтому там,
   *  где доски нет (таблица задач в профиле), обработчика не передают — и кнопки
   *  тоже нет. Мёртвых кнопок в портале быть не должно. */
  onPickParent?: () => void;
  onDelete: () => void; onArchive?: () => void; inline?: boolean;
}) {
  const { user } = useAuth();
  const [t, setT] = useState<TeamTask>(task);
  // Окно оценки: открыто/закрыто и то, что набрано в двух полях.
  const [estOpen, setEstOpen] = useState(false);
  const [estH, setEstH] = useState("");
  const [estM, setEstM] = useState("");
  const [tagsOpen, setTagsOpen] = useState(false);
  const [tagQuery, setTagQuery] = useState("");
  // Справочник тегов = теги, которые уже стоят на задачах портала. Чаще
  // встречающиеся выше: так дубли («wb» рядом с «WB») не расползаются дальше.
  const knownTags = useMemo(() => {
    const счёт = new Map<string, number>();
    for (const задача of allTasks) for (const тег of задача.tags ?? []) счёт.set(тег, (счёт.get(тег) ?? 0) + 1);
    return [...счёт.entries()].sort((a, b) => b[1] - a[1] || a[0].localeCompare(b[0], "ru")).map(([тег]) => тег);
  }, [allTasks]);
  // Сохранение оценки: зовут и кнопка «Оценить», и Enter в любом из двух полей.
  const saveEstimate = () => {
    const ч = Math.max(0, parseInt(estH || "0", 10) || 0);
    const м = Math.min(59, Math.max(0, parseInt(estM || "0", 10) || 0));
    const всего = ч * 60 + м;
    if (всего !== t.estimate_min) void patch({ estimate_min: всего });
    setEstOpen(false);
  };
  const [refsOpen, setRefsOpen] = useState(false);
  // Вид раздела файлов: списком или плиткой — переключатель в шапке, как у них.
  const [filesView, setFilesView] = useState<"list" | "grid">("list");
  // ⚠️ ИСТОРИЯ ЗАДАЧИ (Максим, 02.09.2026, образец Weeek). Грузится ТОЛЬКО при
  // первом раскрытии раздела: на карточку и так приходит четыре запроса, а
  // журнал нужен не каждый раз.
  const [histOpen, setHistOpen] = useState(false);
  const [hist, setHist] = useState<TaskEvent[] | null>(null);
  useEffect(() => {
    if (!histOpen || hist) return;
    api.taskHistory(t.id)
      .then(setHist)
      .catch(() => { toast.error("Не получилось загрузить историю задачи"); setHistOpen(false); });
  }, [histOpen, hist, t.id]);
  // Действия внутри карточки историю меняют — сбрасываем, чтобы не показывать
  // старое. По `updated_at` задачи: он меняется на любой правке, а список
  // вложений и подзадач объявлен ниже.
  useEffect(() => { setHist(null); }, [t.updated_at]);
  // ⚠️ ФАЙЛ МОЖНО ПРОСТО БРОСИТЬ НА КАРТОЧКУ (образец Weeek, 02.09.2026). У них
  // карточка при этом делится на зоны «обложка / описание / комментарии /
  // файлы»; у нас из этого есть только файлы, поэтому зона одна — и подписана,
  // чтобы человек видел, куда именно попадёт файл, и какой предел размера.
  // Счётчик глубины нужен из-за dragleave у детей: без него зона мигает.
  const [dropDepth, setDropDepth] = useState(0);
  // Подстраховка: если файл бросили мимо панели или перетаскивание оборвалось,
  // событие ухода до нас может не дойти — гасим зону по общим событиям окна.
  useEffect(() => {
    const сброс = () => setDropDepth(0);
    window.addEventListener("dragend", сброс);
    window.addEventListener("drop", сброс);
    return () => { window.removeEventListener("dragend", сброс); window.removeEventListener("drop", сброс); };
  }, []);
  const тащатФайл = (e: React.DragEvent) => Array.from(e.dataTransfer?.types ?? []).includes("Files");
  const [atts, setAtts] = useState<TeamAttachment[]>(task.attachments ?? []);  // референсы задачи
  // Подзадачи развёрнуты сразу — как в карточке Weeek: там список виден без
  // единого клика, и по нему сразу понятно, что осталось.
  const [subsOpen, setSubsOpen] = useState(true);
  const [newSub, setNewSub] = useState("");
  // ⚠️ ПРАВКА ПОДЗАДАЧИ — ПРЯМО В СТРОКЕ (образец Weeek, скрин Максима 01.09.2026:
  // «как у них редактируется подзадача, а у нас сложная схема»). Раньше клик по
  // названию ОТКРЫВАЛ подзадачу отдельной задачей в новой панели: чтобы
  // исправить опечатку, человек уходил с карточки и возвращался обратно.
  // Теперь строка на месте превращается в поле: Enter — сохранить, Esc —
  // отменить, уход из поля тоже сохраняет (набранное не пропадает).
  const [editSub, setEditSub] = useState<number | null>(null);
  const [editSubText, setEditSubText] = useState("");
  const [addingSub, setAddingSub] = useState(false);
  const [dragSub, setDragSub] = useState<number | null>(null);   // перетаскиваемая подзадача
  const [overSub, setOverSub] = useState<number | null>(null);   // подзадача под курсором
  const fileRef = useRef<HTMLInputElement>(null);
  const panelRef = useRef<HTMLDivElement>(null);
  // Подзадачи = задачи-дети (parent_task_id == текущая). Берём из общего списка доски.
  const subs = allTasks.filter((x) => x.parent_task_id === t.id);

  // Отделы — для ячейки «Заказчик / отдел» (выбор отдела вместо заметки).
  const [depts, setDepts] = useState<Department[]>([]);
  useEffect(() => { api.getDepartments().then(setDepts).catch(() => {}); }, []);


  async function patch(p: Partial<{
    title: string; description: string; priority: TaskPriority; status: TeamTaskStatus; start_date: string; due_date: string;
    // Поля карточки по образцу Weeek (01.09.2026).
    tags: string[]; task_type: string; estimate_min: number; link: string; category: string;
  }>) {
    try { const u = await api.patchTask(t.id, p); setT(u); onChanged(); } catch { /* тихо */ }
  }
  // Референсы задачи (вложения, parent_type="task").
  async function onPickFile(files: FileList | null) {
    if (!files?.length) return;
    for (const f of Array.from(files)) {
      try { const a = await api.uploadTeamFile("task", t.id, f); setAtts((p) => [...p, a]); } catch { /* тихо */ }
    }
    setRefsOpen(true);
  }
  async function addLink() {
    const url = window.prompt("Ссылка-референс:"); if (!url?.trim()) return;
    try { const a = await api.addTeamLink("task", t.id, url.trim()); setAtts((p) => [...p, a]); setRefsOpen(true); } catch { /* тихо */ }
  }
  async function delRef(id: number) { try { await api.deleteTeamAttachment(id); setAtts((p) => p.filter((a) => a.id !== id)); } catch { /* тихо */ } }
  async function editRef(a: TeamAttachment) {
    const title = window.prompt("Название референса:", a.original_name || "");
    if (title === null) return;
    const body: { title: string; url?: string } = { title: title.trim() };
    if (a.kind === "link") {
      const url = window.prompt("Ссылка:", a.url || "");
      if (url === null) return;
      if (url.trim()) body.url = url.trim();
    }
    try { const u = await api.patchTeamAttachment(a.id, body); setAtts((p) => p.map((x) => (x.id === a.id ? u : x))); } catch { /* тихо */ }
  }
  // Подзадачи (задачи-дети)
  async function addSub() {
    const title = newSub.trim(); if (!title || !user) return;
    // Поле НЕ закрывается: у них после Enter курсор остаётся, и список набивается
    // подряд, без возврата мышью к кнопке «плюс» после каждой строки.
    try { await api.createTask({ title, assignee_id: user.id, parent_task_id: t.id }); setNewSub(""); onChanged(); } catch { /* тихо */ }
  }
  async function toggleSub(s: TeamTask) {
    try { await api.patchTask(s.id, { status: s.status === "done" ? "new" : "done" }); onChanged(); } catch { /* тихо */ }
  }
  async function saveSubTitle(s: TeamTask) {
    const title = editSubText.trim();
    setEditSub(null);
    if (!title || title === s.title) return;
    try { await api.patchTask(s.id, { title }); onChanged(); } catch { /* тихо */ }
  }
  async function delSub(id: number) { try { await api.deleteTask(id); onChanged(); } catch { /* тихо */ } }
  async function reorderSub(targetId: number) {
    const ids = subs.map((x) => x.id);
    const from = ids.indexOf(dragSub ?? -1), to = ids.indexOf(targetId);
    setDragSub(null); setOverSub(null);
    if (from < 0 || to < 0 || from === to) return;
    const [m] = ids.splice(from, 1); ids.splice(to, 0, m);
    try { await api.reorderTasks(ids); onChanged(); } catch { /* тихо */ }
  }
  const subsDone = subs.filter((s) => s.status === "done").length;

  // Перенос задачи в подзадачи: выбор родителя кликом по карточке на доске.
  const otherTasks = allTasks.filter((x) => x.id !== t.id && x.parent_task_id !== t.id);
  const selectableDepts = depts.filter((d) => d.title !== "Founder" && d.key !== "founder");
  const deptOptions = [
    ...(t.category && !selectableDepts.some((d) => d.title === t.category) ? [{ value: t.category, label: t.category }] : []),
    ...selectableDepts.map((d) => ({ value: d.title, label: d.title })),
  ];

  const inputCls = INPUT_CLS;

  // Референсы — отдельный раскрывающийся пункт (файлы/ссылки на задачу),
  // ставится между «Постановка» и «Проектирование».
  const refsBlock = (
    <div key="__refs" className="rounded-[var(--radius-md)]">
      <button type="button" onClick={() => setRefsOpen((v) => !v)}
        className="glass-flat flex w-full items-center gap-2 rounded-[var(--radius-md)] px-1 py-1 text-left hover:bg-[var(--hover)]">
        {refsOpen ? <IconChevronDown size={13} className="shrink-0 text-[var(--textMuted)]" /> : <IconChevronRight size={13} className="shrink-0 text-[var(--textMuted)]" />}
        {/* ⚠️ Заголовок раздела — ОБЫЧНЫМ цветом текста. Был фирменным фиолетовым,
            а он в портале означает действие или ссылку: «Файлы» и «Подзадачи»
            выглядели нажимаемыми ссылками, хотя это подписи (Максим, 02.09.2026).
            В образце они тоже нейтральные. */}
        <span className="text-[12px] font-semibold text-[var(--text)]">Файлы</span>
        <span className="flex-1" />
        <span className="shrink-0 text-[11px] tabular-nums text-[var(--textMuted)]">{atts.length}</span>
      </button>
      {refsOpen && (
        <div className="ml-4 border-l border-[var(--border)] pl-3 pb-1.5 pt-1">
          {/* Шапка раздела — как у них: «Прикрепить файлы» и переключатель
              «список / плитка». Ссылка рядом: у нас в этом же разделе живут и
              ссылки, отдельного раздела «Связи» в портале нет. */}
          {canEdit && (
            <div className="mb-1 flex items-center gap-2">
              <button type="button" onClick={() => fileRef.current?.click()}
                className="glass-hover glass-flat inline-flex items-center gap-1 rounded-[var(--radius-md)] px-1.5 py-0.5 text-[11px] text-[var(--textMuted)]">
                <IconAttach size={11} /> Прикрепить файлы
              </button>
              <button type="button" onClick={() => void addLink()}
                className="glass-hover glass-flat inline-flex items-center gap-1 rounded-[var(--radius-md)] px-1.5 py-0.5 text-[11px] text-[var(--textMuted)]">
                <IconLink size={11} /> Ссылка
              </button>
              <span className="flex-1" />
              <span className="inline-flex overflow-hidden rounded-[var(--radius-md)] border border-[var(--border)]">
                {(["list", "grid"] as const).map((v) => (
                  <button key={v} type="button" onClick={() => setFilesView(v)}
                    aria-pressed={filesView === v} data-tooltip={v === "list" ? "Списком" : "Плиткой"}
                    className="glass-hover glass-flat px-1.5 py-0.5"
                    style={filesView === v ? { background: "var(--hover)", color: "var(--text)" } : { color: "var(--textMuted)" }}>
                    {v === "list" ? <Rows3 size={12} /> : <LayoutGrid size={12} />}
                  </button>
                ))}
              </span>
            </div>
          )}
          {atts.length === 0 && <div className="mb-1 text-[11px] text-[var(--textMuted)]">Файлов пока нет</div>}
          <FileList items={atts} canEdit={canEdit} view={filesView}
            onDelete={(id) => void delRef(id)} onEdit={(a) => void editRef(a)}
            onAdd={() => fileRef.current?.click()} />
        </div>
      )}
    </div>
  );

  const fileInput = (
    <input ref={fileRef} type="file" multiple className="hidden" onChange={(e) => { void onPickFile(e.target.files); e.target.value = ""; }} />
  );
  const dropHandlers = canEdit ? {
    onDragEnter: (e: React.DragEvent) => { if (тащатФайл(e)) { e.preventDefault(); setDropDepth((d) => d + 1); } },
    onDragOver: (e: React.DragEvent) => { if (тащатФайл(e)) e.preventDefault(); },
    // ⚠️ На dragleave НЕ проверяем типы: браузер иногда отдаёт это событие без
    // dataTransfer, и зона оставалась висеть на экране навсегда.
    onDragLeave: () => setDropDepth((d) => Math.max(0, d - 1)),
    onDrop: (e: React.DragEvent) => {
      if (!тащатФайл(e)) return;
      e.preventDefault();
      setDropDepth(0);
      setRefsOpen(true);
      void onPickFile(e.dataTransfer.files);
    },
  } : {};

  const dropZone = dropDepth > 0 ? (
    <div className="file-drop pointer-events-none absolute inset-2 z-10 grid place-items-center rounded-[var(--radius-lg)] text-center">
      <span>
        <IconAttach size={20} className="mx-auto mb-2 text-[var(--primary)]" />
        <span className="block text-[12px] font-semibold text-[var(--text)]">Отпустите — приложим к задаче</span>
        <span className="mt-1 block text-[11px] text-[var(--textMuted)]">Любой формат, не более 25 МБ</span>
      </span>
    </div>
  ) : null;

  const body = (
    <>
        {/* ⚠️ ВЕРХНЯЯ ПОЛОСА — как в Weeek: номер задачи, ссылка на неё и
            закрытие. Номер даёт то, чего в портале не было вовсе: на задачу
            нельзя было сослаться. Берём id — он уже уникален; ссылка ведёт на
            «Кабинет» с открытой задачей. */}
        <div className="flex items-center gap-2 border-b border-[var(--border)] px-4 py-2.5">
          <span className="text-[12px] font-semibold tabular-nums text-[var(--textMuted)]">#{t.id}</span>
          <button type="button" data-tooltip="Скопировать ссылку на задачу"
            onClick={() => { void navigator.clipboard?.writeText(`${window.location.origin}/cabinet?task=${t.id}`); }}
            className="glass-flat text-[var(--textMuted)] transition hover:text-[var(--text)]">
            <IconLink size={14} />
          </button>
          <span className="flex-1" />
          <button type="button" onClick={onClose} data-tooltip="Закрыть"
            className="glass-flat text-[var(--textMuted)] transition hover:text-[var(--text)]">
            <IconClose size={16} />
          </button>
        </div>

        {/* Шапка: статус + приоритет чипами, крупное название, чипы автор/дата */}
        <div className="border-b border-[var(--border)] p-4">
          <div className="mb-2 flex items-center gap-1.5">
            {/* Статус-чип (редактируемый select, стилизован под пилюлю) */}
            <Badge
              color={stageColor(t.status)}
              className="relative"
              after={canEdit && (
                <Select
                  value={t.status}
                  onChange={(v) => void patch({ status: v as TeamTaskStatus })}
                  size="xs"
                  options={[
                    { value: "backlog", label: "Бэклог" },
                    { value: "new", label: "Новая" },
                    { value: "in_progress", label: "В работе" },
                    { value: "review", label: "Согласование" },
                    { value: "done", label: "Готово" },
                  ]}
                  // Список открывается по клику на сам бейдж: триггер невидим и лежит поверх.
                  trigger={<button type="button" className="absolute inset-0 cursor-pointer opacity-0" aria-label="Статус" />}
                />
              )}
            >
              {stageLabelOne(t.status)}
            </Badge>
            {t.priority ? (
            <Badge
              color={prioColor(t.priority)}
              icon={<Flag size={10} />}
              className="relative"
              after={canEdit && (
                <Select
                  value={t.priority}
                  onChange={(v) => void patch({ priority: v as TaskPriority })}
                  size="xs"
                  options={PRIO_OPTIONS}
                  trigger={<button type="button" className="absolute inset-0 cursor-pointer opacity-0" aria-label="Приоритет" />}
                />
              )}
            >
              {prioLabel(t.priority)}
            </Badge>
            ) : null}
            <span className="flex-1" />
            {/* Автор + дата создания + аватар — как в карточке заявки на доработку */}
            <div className="flex items-center gap-1.5">
              <div className="text-right leading-tight">
                <div className="text-[11px] font-semibold text-[var(--text)]">{t.author_name || "—"}</div>
                <div className="text-[11px] text-[var(--textMuted)] tabular-nums">{fmtDateTime(t.created_at)}</div>
              </div>
              <AuthorAvatar name={t.author_name} size={26} />
            </div>
          </div>

          {/* «Выполнить» — как в их карточке: одно нажатие закрывает задачу,
              не открывая список статусов. Для задачи в «Готово» кнопка
              возвращает её в работу. */}
          {canEdit && (
            <button type="button"
              onClick={() => void patch({ status: t.status === "done" ? "in_progress" : "done" })}
              className="glass-hover mb-2 inline-flex h-[30px] items-center gap-1.5 rounded-[var(--radius-md)] border px-3 text-[12px] font-semibold"
              style={{
                borderColor: t.status === "done" ? "var(--border)" : "color-mix(in srgb, var(--success) 45%, transparent)",
                color: t.status === "done" ? "var(--textMuted)" : "var(--successInk)",
              }}>
              <IconCheck size={13} /> {t.status === "done" ? "Вернуть в работу" : "Выполнить"}
            </button>
          )}
          {/* ⚠️ ЗАГОЛОВОК — ТЕКСТ, А НЕ ПОЛЕ (Максим, 01.09.2026: «у них имя задачи
              не в рамке, а красиво написано, будто это не поле ввода»). Правится
              по клику, но ни рамки, ни подложки: рамка вокруг названия делала
              карточку похожей на форму. Кегль 20/700 — по их карточке и по нашей
              шкале (docs/typography.md, заголовок экрана). */}
          {canEdit ? (
            <textarea ref={autoGrow} rows={1} defaultValue={t.title}
              onInput={(e) => autoGrow(e.currentTarget)}
              onBlur={(e) => { const v = e.target.value.trim(); if (v && v !== t.title) void patch({ title: v }); }}
              // ⚠️ КЕГЛЬ И ВЕС — ИНЛАЙНОМ. В globals.css есть незалойеренное
              // правило `button, input, select, textarea { font: … }`, оно
              // перебивает утилиты Tailwind: `text-[20px] font-bold` на textarea
              // не действовали, и заголовок оставался 12/400 — ровно как подписи
              // полей (замер 01.09.2026). Инлайн сильнее любого класса.
              style={{ fontSize: 20, fontWeight: 700, lineHeight: 1.2 }}
              className="mb-2 w-full resize-none overflow-hidden border-0 bg-transparent p-0 text-[var(--text)] outline-none focus:ring-0" />
          ) : <div className="mb-2 text-[20px] font-bold leading-tight text-[var(--text)]">{t.title}</div>}
          {/* ⚠️ ПОЛЯ СТОЛБИКОМ, КАК В КАРТОЧКЕ WEEEK (разбор 01.09.2026, Максим:
              «сделать в точности как у них… не забудь поля и функции, типа
              теги»). У них слева значок с подписью, справа значение, и каждое
              поле правится кликом по значению. Раньше это была строка чипов —
              на узкой панели она разъезжалась и порядок полей плавал. */}
          <div className="flex flex-col gap-0.5">
            <Поле подпись="Исполнители">
              {(() => {
                const люди = t.assignees?.length ? t.assignees : (t.assignee_name ? [{ id: t.assignee_id, name: t.assignee_name, avatar: "" }] : []);
                if (!люди.length) return <span className="text-[var(--textMuted)]">Назначить…</span>;
                return (
                  <span className="flex flex-wrap items-center gap-1.5">
                    {люди.map((a) => (
                      <span key={a.id} className="inline-flex items-center gap-1">
                        <UserAvatar name={a.name} avatarUrl={a.avatar} userId={a.id} size={18} fillBg />
                        <span className="text-[var(--text)]">{a.name}</span>
                      </span>
                    ))}
                  </span>
                );
              })()}
            </Поле>

            {/* ⚠️ Поле правит ОТДЕЛ ЗАДАЧИ (её категорию — тот самый чип на
                карточке). Раньше оно было привязано к ячейке этапа «Заказчик /
                отдел» из удалённого модуля и потому всегда показывало «—»:
                выбрать было нечего, сохранять некуда. */}
            <Поле подпись="Проект">
              {canEdit ? (
                <Select value={t.category} onChange={(v) => void patch({ category: v })}
                  options={[{ value: "", label: "Без проекта" }, ...deptOptions]} placeholder="Выбрать проект…" size="sm" />
              ) : <span className="text-[var(--textMuted)]">{t.category || "—"}</span>}
            </Поле>

            <Поле подпись="Дата">
              {canEdit ? (
                // ⚠️ Дата — ТОЛЬКО через `DatePicker` портала. Свой поповер с
                // календарём тут уже был: те же кнопки «Очистить»/«Сегодня»,
                // но своим видом и своим кеглем.
                <span className="flex items-center gap-2">
                  {/* ⚠️ СРОК — ОТРЕЗОК: начало и окончание (Максим, 01.09.2026:
                      «у них есть deadline у подзадач», на скрине их календарь с
                      полями «Дата начала» и «Дата окончания»). Была одна дата,
                      поэтому «когда начинаем» записать было негде. Элемент —
                      стандартный `DateRangePicker` портала. */}
                  {/* ⚠️ ДАТА КОРОТКО: «21.09 — 25.09» (Максим, 02.09.2026). Полное
                      «10 сентября 2026 — 10 сентября 2026» в поле шириной 240 px
                      не помещалось и обрезалось многоточием — вторую дату было
                      не прочитать. Год печатаем, только если срок выходит за
                      текущий: иначе «21.09.27» читалось бы как лишний шум. */}
                  <DateRangePicker
                    value={{ from: t.start_date ?? "", to: t.due_date ?? "" }}
                    onChange={(v) => void patch({ start_date: v.from, due_date: v.to })}
                    displayFormat={[t.start_date, t.due_date].filter(Boolean)
                      .every((d) => d.slice(0, 4) === String(new Date().getFullYear())) ? "dd.MM" : "dd.MM.yy"}
                    placeholder="Выбрать срок…" size="sm" numberOfMonths={1} />
                  {/* Просрочка плашкой — приём их карточки: сразу видно, на сколько опоздали. */}
                  {t.due_date && t.status !== "done" && t.due_date < toIsoDate(new Date()) && (
                    <span className="shrink-0 rounded-[var(--radius-md)] px-1.5 py-0.5 text-[11px]"
                          style={{ background: "color-mix(in srgb, var(--danger) 16%, transparent)", color: "var(--dangerInk)" }}>
                      {Math.round((Date.now() - new Date(t.due_date).getTime()) / 864e5)} дн.
                    </span>
                  )}
                </span>
              ) : <span>{t.due_date ? fmtDate(t.due_date) : "—"}</span>}
            </Поле>

            <Поле подпись="Оценка времени">
              {canEdit ? (
                // ⚠️ ОЦЕНКА — ПОПАПОМ У САМОГО ПОЛЯ, не окном по центру экрана
                // (Максим, 02.09.2026: «окно очень большое для ввода времени»).
                // Два числа не стоят модалки с подложкой: поповер кита встаёт
                // под полем, закрывается по Esc и клику мимо, и живёт на слое
                // выпадающих — выше панели задачи, разбираться со слоями не надо.
                // `glass-flat` — отказ от общей тени кнопок: иначе поле-кнопка
                // в тёмной теме отличается от соседних полей ореолом.
                <Popover open={estOpen} onOpenChange={(v) => {
                  if (v) {
                    setEstH(t.estimate_min ? String(Math.floor(t.estimate_min / 60)) : "");
                    setEstM(t.estimate_min ? String(t.estimate_min % 60) : "");
                  }
                  setEstOpen(v);
                }}>
                  <PopoverAnchor asChild>
                    <button type="button" onClick={() => setEstOpen(true)}
                      className={`glass-flat ${INPUT_CLS} text-left ${t.estimate_min ? "" : "text-[var(--textMuted)]"}`}>
                      {fmtEstimate(t.estimate_min) || "Оценить…"}
                    </button>
                  </PopoverAnchor>
                  {/* Ширина — РОВНО по полю (`--radix-popover-trigger-width`),
                      как у списка «Тип» и «Приоритет»: попап не должен быть
                      уже или шире строки, к которой он относится
                      (Максим, 02.09.2026). */}
                  <PopoverContent align="start" className="w-[var(--radix-popover-trigger-width)] p-3">
                    <div className="flex items-end gap-2">
                      <label className="min-w-0 flex-1">
                        <span className="mb-1 block text-[11px] text-[var(--textMuted)]">Часы</span>
                        <Input value={estH} onChange={(e) => setEstH(e.target.value.replace(/\D/g, "").slice(0, 3))}
                               onKeyDown={(e) => { if (e.key === "Enter") saveEstimate(); }}
                               inputMode="numeric" placeholder="00" autoFocus />
                      </label>
                      <label className="min-w-0 flex-1">
                        <span className="mb-1 block text-[11px] text-[var(--textMuted)]">Минуты</span>
                        <Input value={estM} onChange={(e) => setEstM(e.target.value.replace(/\D/g, "").slice(0, 2))}
                               onKeyDown={(e) => { if (e.key === "Enter") saveEstimate(); }}
                               inputMode="numeric" placeholder="00" />
                      </label>
                    </div>
                    <div className="mt-3 flex justify-end">
                      <Button variant="primary" onClick={saveEstimate}>Оценить</Button>
                    </div>
                  </PopoverContent>
                </Popover>
              ) : <span>{fmtEstimate(t.estimate_min) || "—"}</span>}
            </Поле>

            <Поле подпись="Тип">
              {canEdit ? (
                <Select value={t.task_type} onChange={(v) => void patch({ task_type: v })}
                  options={TYPE_OPTIONS} placeholder="Выбрать…" size="sm" />
              ) : <span>{t.task_type || "—"}</span>}
            </Поле>

            <Поле подпись="Приоритет">
              {/* Цвет приоритета — точкой в самом списке (`icon` у опции), а не
                  своей плашкой поверх выпадашки: выпадашка остаётся обычной
                  выпадашкой портала, а значение всё равно видно с одного взгляда. */}
              {canEdit ? (
                <Select value={t.priority} onChange={(v) => void patch({ priority: v as TaskPriority })}
                  options={PRIO_OPTIONS_CHIP} size="sm" />
              ) : <span>{prioLabel(t.priority)}</span>}
            </Поле>

            <Поле подпись="Теги">
              {canEdit ? (
                // Поле показывает выбранные плашки; список открывается попапом
                // у самого поля — как оценка времени и как у них в образце.
                <Popover open={tagsOpen} onOpenChange={(v) => { setTagsOpen(v); if (v) setTagQuery(""); }}>
                  <PopoverAnchor asChild>
                    <button type="button" onClick={() => setTagsOpen(true)}
                      className={`glass-flat ${INPUT_CLS} flex flex-wrap items-center gap-1 text-left`}>
                      {(t.tags ?? []).length
                        ? t.tags.map((тег) => <TagChip key={тег} тег={тег} />)
                        : <span className="text-[var(--textMuted)]">Пусто…</span>}
                    </button>
                  </PopoverAnchor>
                  <PopoverContent align="start" className="w-[var(--radix-popover-trigger-width)] p-2">
                    {(t.tags ?? []).length > 0 && (
                      <div className="mb-2 flex flex-wrap gap-1 border-b border-[var(--border)] pb-2">
                        {t.tags.map((тег) => (
                          <TagChip key={тег} тег={тег}
                            onRemove={() => void patch({ tags: (t.tags ?? []).filter((x) => x !== тег) })} />
                        ))}
                      </div>
                    )}
                    <Input value={tagQuery} onChange={(e) => setTagQuery(e.target.value)}
                      onKeyDown={(e) => {
                        if (e.key !== "Enter") return;
                        const тег = tagQuery.trim().replace(/^#+/, "");
                        if (!тег || (t.tags ?? []).includes(тег)) return;
                        void patch({ tags: [...(t.tags ?? []), тег] });
                        setTagQuery("");
                      }}
                      placeholder="Теги…" autoFocus />
                    {/* Список — теги, которые уже стоят на задачах портала: чаще
                        встречающиеся выше. Свой справочник заводить незачем. */}
                    <div className="mt-2 flex max-h-[220px] flex-col items-start gap-1 overflow-y-auto">
                      {knownTags
                        .filter((тег) => !(t.tags ?? []).includes(тег) && тег.toLowerCase().includes(tagQuery.trim().toLowerCase()))
                        .slice(0, 40)
                        .map((тег) => (
                          <button key={тег} type="button" className="glass-flat max-w-full"
                            onClick={() => { void patch({ tags: [...(t.tags ?? []), тег] }); setTagQuery(""); }}>
                            <TagChip тег={тег} />
                          </button>
                        ))}
                      {tagQuery.trim() && !knownTags.includes(tagQuery.trim()) && (
                        <span className="px-1 py-0.5 text-[11px] text-[var(--textMuted)]">
                          Enter — создать «{tagQuery.trim()}»
                        </span>
                      )}
                    </div>
                  </PopoverContent>
                </Popover>
              ) : (
                (t.tags ?? []).length
                  ? <span className="flex flex-wrap gap-1">{t.tags.map((тег) => <TagChip key={тег} тег={тег} />)}</span>
                  : <span className="text-[var(--textMuted)]">—</span>
              )}
            </Поле>

            <Поле подпись="Ссылка">
              {canEdit ? (
                <Input defaultValue={t.link} placeholder="Введите ссылку…" type="url"
                  onBlur={(e) => { if (e.target.value !== t.link) void patch({ link: e.target.value.trim() }); }} />
              ) : (t.link
                  ? <a href={t.link} target="_blank" rel="noreferrer" className="truncate text-[var(--primary)]">{t.link}</a>
                  : <span className="text-[var(--textMuted)]">—</span>)}
            </Поле>
          </div>
        </div>

        <div className="min-h-0 flex-1 overflow-y-auto scrollbar-autohide p-4">
          {(canEdit || t.description) && (
            <label className="mb-3 flex flex-col gap-0.5 text-[11px] text-[var(--textMuted)]">Описание
              <textarea ref={autoGrow} rows={1} className={`${TEXTAREA_CLS} resize-none overflow-hidden font-semibold leading-snug text-[var(--text)]`} defaultValue={t.description} disabled={!canEdit}
                placeholder="Описание задачи…"
                onInput={(e) => autoGrow(e.currentTarget)}
                onBlur={(e) => { if (e.target.value !== t.description) void patch({ description: e.target.value }); }} />
            </label>
          )}

          {/* ⚠️ БЛОКА «ЭТАПЫ» БОЛЬШЕ НЕТ. Этапы и ячейки жили в модуле
              «IT-проекты», который убрали 28.07.2026 вместе с таблицами:
              `GET /team/tasks/{id}/cells` с тех пор отвечает 500, ошибку глушил
              `.catch`, и список этапов всегда приходил пустым. Хуже того, файлы
              задачи рисовались ВНУТРИ этого списка (`idx === 0 && refsBlock`) —
              то есть не рисовались вовсе, и прикрепить файл к задаче было
              нельзя (Максим, 02.09.2026). Теперь файлы — своя секция. */}
          <div className="flex flex-col gap-1">
            {refsBlock}

            {/* Подзадачи — раскрывающийся чек-лист */}
            <div className="rounded-[var(--radius-md)]">
              <button type="button" onClick={() => setSubsOpen((v) => !v)}
                className="glass-flat flex w-full items-center gap-2 rounded-[var(--radius-md)] px-1 py-1 text-left hover:bg-[var(--hover)]">
                {subsOpen ? <IconChevronDown size={13} className="shrink-0 text-[var(--textMuted)]" /> : <IconChevronRight size={13} className="shrink-0 text-[var(--textMuted)]" />}
                <span className="text-[12px] font-semibold text-[var(--text)]">Подзадачи</span>
                {/* Счётчик стоит СРАЗУ ЗА НАЗВАНИЕМ и со значком-кружком — как в
                    образце. У правого края он читался как отдельная цифра «ни о
                    чём» (Максим, 02.09.2026). */}
                <span className="inline-flex shrink-0 items-center gap-1 text-[11px] tabular-nums text-[var(--textMuted)]">
                  <CircleDot size={11} /> {subsDone}/{subs.length}
                </span>
                <span className="flex-1" />
              </button>
              {subsOpen && (
                <div className="ml-4 flex flex-col gap-1.5 border-l border-[var(--border)] pl-3 pb-1.5 pt-1.5">
                  {subs.map((s) => {
                    const sd = s.status === "done";
                    return (
                    <div key={s.id} draggable={canEdit}
                      onDragStart={canEdit ? (e) => { setDragSub(s.id); e.dataTransfer.effectAllowed = "move"; } : undefined}
                      onDragOver={canEdit && dragSub != null ? (e) => { e.preventDefault(); setOverSub(s.id); } : undefined}
                      onDragLeave={() => setOverSub((o) => (o === s.id ? null : o))}
                      onDrop={canEdit && dragSub != null ? () => void reorderSub(s.id) : undefined}
                      onDragEnd={() => { setDragSub(null); setOverSub(null); }}
                      /* ⚠️ Строка ОТЗЫВАЕТСЯ НА КУРСОР подложкой. Значки правки
                         и срока появляются при наведении, но заметить это было
                         нечем: строка под курсором выглядела точно так же, и
                         Максим 01.09.2026 написал «при наведении не вижу ни
                         карандашика, ни где поставят у дедлайна». Подложка —
                         тот же отклик, что у строк таблиц во всём портале. */
                      className={`subtask-row group flex items-center gap-2 rounded-[var(--radius-md)] px-1.5 py-1 transition ${editSub === s.id ? "border border-[var(--border)] px-1.5 py-1" : ""} ${overSub === s.id && dragSub !== s.id ? "ring-1 ring-[var(--primary)]" : ""} ${dragSub === s.id ? "opacity-40" : ""}`}>
                      {/* Ручка перетаскивания — под курсором, как значки правки:
                          в покое список чистый, как у них. */}
                      {canEdit && <GripVertical size={12} className="subtask-act shrink-0 cursor-grab text-[var(--textMuted)]" />}
                      <button type="button" disabled={!canEdit} onClick={() => void toggleSub(s)}
                        className="grid h-[18px] w-[18px] shrink-0 place-items-center rounded-full border transition"
                        style={{ borderColor: sd ? "#10b981" : "var(--border)", background: sd ? "#10b981" : "transparent" }}
                        data-tooltip={sd ? "Снять отметку" : "Отметить выполненной"}>
                        {sd && <IconCheck size={10} className="text-white" />}
                      </button>
                      {editSub === s.id ? (
                        /* Строка в правке: рамка вокруг всей строки и подсказка
                           «Enter для сохранения» — ровно как у них. */
                        <span className="min-w-0 flex-1">
                          <input autoFocus value={editSubText} maxLength={250}
                            onChange={(e) => setEditSubText(e.target.value)}
                            onKeyDown={(e) => {
                              if (e.key === "Enter") { e.preventDefault(); void saveSubTitle(s); }
                              if (e.key === "Escape") { e.preventDefault(); setEditSub(null); }
                            }}
                            onBlur={() => void saveSubTitle(s)}
                            className="w-full bg-transparent text-[12px] text-[var(--text)] outline-none" />
                          <span className="mt-0.5 block text-[11px] text-[var(--textMuted)]">
                            <kbd>Enter</kbd> для сохранения
                          </span>
                        </span>
                      ) : (
                        <button type="button"
                          onClick={() => { if (!canEdit) return; setEditSub(s.id); setEditSubText(s.title); }}
                          className={`glass-flat min-w-0 flex-1 truncate text-left text-[12px] ${sd ? "text-[var(--textMuted)] line-through" : "text-[var(--text)]"}`}
                          >{s.title}</button>
                      )}
                      {/* ⚠️ КАРАНДАШ ПОЯВЛЯЕТСЯ ПОД КУРСОРОМ (Максим, 02.09.2026),
                          как в образце Weeek: в покое список чистый. 01.09 значки
                          сделали видимыми всегда — тогда причиной сочли бледность,
                          а настоящая была в @media (hover: hover) у классов
                          Tailwind. Теперь показ живёт в globals.css обычным
                          :hover (`.subtask-act`) и работает на любой мыши.
                          Название кликается по-прежнему — карандаш это подсказка,
                          а не единственный путь. */}
                      {canEdit && editSub !== s.id && (
                        <button type="button"
                          onClick={() => { setEditSub(s.id); setEditSubText(s.title); }}
                          className="subtask-act glass-flat shrink-0 text-[var(--text)]"
                          data-tooltip="Изменить название"><IconEdit size={14} /></button>
                      )}
                      {/* Срок подзадачи правится прямо здесь — у них в строке
                          стоят быстрые значки (дата, исполнитель, время). Дата —
                          самое частое, поэтому она первой. */}
                      {/* Срок подзадачи — ТОТ ЖЕ элемент, что и у задачи, только
                          компактный: в строке списка рамка поля лишняя. Пока не
                          задан, показывает «Срок» приглушённым; просрочка красная. */}
                      {canEdit ? (
                        <span className={`shrink-0 text-[11px] transition ${s.due_date || s.start_date ? "" : "subtask-act"} ${s.due_date && !sd && s.due_date < toIsoDate(new Date()) ? "text-[var(--dangerInk)]" : ""}`}
                              data-tooltip="Срок подзадачи">
                          <DateRangePicker
                            value={{ from: s.start_date ?? "", to: s.due_date ?? "" }}
                            onChange={(v) => { void api.patchTask(s.id, { start_date: v.from, due_date: v.to }).then(onChanged).catch(() => {}); }}
                            variant="inline" numberOfMonths={1} align="right" displayFormat="d MMM"
                            placeholder={<CalendarDays size={14} className="text-[var(--text)]" />} />
                        </span>
                      ) : (s.due_date && <span className="shrink-0 text-[11px] tabular-nums text-[var(--textMuted)]">до {fmtDate(s.due_date)}</span>)}
                      {/* Открыть подзадачу отдельной задачей — теперь значком, а
                          не кликом по названию: клик по названию занят правкой. */}
                      <button type="button" onClick={(e) => onOpenTask(s, e.currentTarget.getBoundingClientRect())}
                        className="subtask-act glass-flat shrink-0 text-[var(--text)]"
                        data-tooltip="Открыть как задачу"><ArrowRight size={14} /></button>
                      {canEdit && (
                        <button type="button" onClick={() => void delSub(s.id)}
                          className="subtask-act subtask-act-danger glass-flat shrink-0 text-[var(--text)]" data-tooltip="Удалить подзадачу"><IconClose size={14} /></button>
                      )}
                    </div>
                  );
                  })}
                  {subs.length === 0 && <div className="text-[11px] text-[var(--textMuted)]">Подзадач пока нет</div>}
                  {/* ⚠️ ДОБАВЛЕНИЕ — ОДНОЙ СТРОКОЙ, как у них: приглушённая строка
                      «Новая задача…», по клику она же становится полем. Раньше
                      здесь стояла пара «поле + синяя кнопка +»: две мишени вместо
                      одной, и кнопка требовала возврата мышью после каждой строки. */}
                  {canEdit && (addingSub ? (
                    <div className="flex w-full items-start gap-2 rounded-[var(--radius-md)] border border-[var(--border)] px-1.5 py-1.5">
                      {/* ⚠️ Значок ТОТ ЖЕ, что в покое (⊕), и подпись та же —
                          строка не превращается во что-то другое, а просто даёт
                          печатать (образец Weeek, скрины Максима 02.09.2026).
                          Был пустой кружок подзадачи: строка на глазах меняла
                          смысл. */}
                      <CirclePlus size={14} className="mt-[3px] shrink-0 text-[var(--textMuted)]" />
                      <span className="min-w-0 flex-1">
                        <input autoFocus value={newSub} maxLength={250}
                          onChange={(e) => setNewSub(e.target.value)}
                          onKeyDown={(e) => {
                            if (e.key === "Enter") { e.preventDefault(); void addSub(); }
                            if (e.key === "Escape") { e.preventDefault(); setNewSub(""); setAddingSub(false); }
                          }}
                          onBlur={() => { if (!newSub.trim()) setAddingSub(false); }}
                          placeholder="Новая задача…"
                          className="w-full bg-transparent text-[12px] text-[var(--text)] outline-none placeholder:text-[var(--textMuted)]" />
                        {/* Подсказка видна всё время, пока открыт ввод, — как у них. */}
                        <span className="mt-0.5 block text-[11px] text-[var(--textMuted)]">
                          <kbd>Enter</kbd> для создания
                        </span>
                      </span>
                    </div>
                  ) : (
                    <button type="button" onClick={() => setAddingSub(true)}
                      className="glass-flat flex items-center gap-1.5 rounded-[var(--radius-md)] py-1 text-left text-[12px] text-[var(--textMuted)] hover:text-[var(--text)]">
                      <CirclePlus size={14} className="shrink-0" /> Новая задача…
                    </button>
                  ))}
                </div>
              )}
            </div>
            {/* ⚠️ ИСТОРИЯ ЗАДАЧИ — как у них: кто, что и когда. Раздел свёрнут
                по умолчанию и грузится при первом раскрытии. */}
            <div className="rounded-[var(--radius-md)]">
              <button type="button" onClick={() => setHistOpen((v) => !v)}
                className="glass-flat flex w-full items-center gap-2 rounded-[var(--radius-md)] px-1 py-1 text-left">
                {histOpen ? <IconChevronDown size={13} className="shrink-0 text-[var(--textMuted)]" /> : <IconChevronRight size={13} className="shrink-0 text-[var(--textMuted)]" />}
                <span className="text-[12px] font-semibold text-[var(--text)]">История</span>
                <span className="flex-1" />
                {hist && <span className="shrink-0 text-[11px] tabular-nums text-[var(--textMuted)]">{hist.length}</span>}
              </button>
              {histOpen && (
                <div className="ml-4 flex flex-col gap-2 border-l border-[var(--border)] pl-3 pb-1.5 pt-1.5">
                  {!hist && <span className="text-[11px] text-[var(--textMuted)]">Смотрим историю…</span>}
                  {hist?.length === 0 && <span className="text-[11px] text-[var(--textMuted)]">Пока ничего не менялось</span>}
                  {hist?.map((e) => {
                    const подробность = подробностьСобытия(e);
                    return (
                      <div key={e.id} className="flex items-start gap-2">
                        <span className="min-w-0 flex-1">
                          <span className="text-[12px] text-[var(--textMuted)]">
                            <span className="font-semibold text-[var(--text)]">{e.actor_name || "—"}</span>{" "}
                            {фразаСобытия(e)}
                          </span>
                          {подробность && (
                            <span className="mt-0.5 block truncate border-l-2 border-[var(--border)] pl-2 text-[11px] text-[var(--text)]">
                              {подробность}
                            </span>
                          )}
                        </span>
                        <span className="shrink-0 text-[11px] text-[var(--textMuted)]">{датаСобытия(e.created_at)}</span>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>

          </div>

          {/* Действия: «в подзадачи» (только на доске) + архив (вместо удаления) + удалить */}
          {canEdit && (
            <div className="mt-4 flex items-center gap-2 border-t border-[var(--border)] pt-3">
              {!inline && onPickParent && otherTasks.length > 0 && (
                <Button onClick={onPickParent}>
                  ↳ в подзадачи
                </Button>
              )}
              <span className="flex-1" />
              {onArchive && !task.archived_at && (
                <Button title="Закрыть задачу: убрать с доски в архив (подзадачи уйдут вместе с ней)" onClick={onArchive}>
                  <Archive size={12} /> В архив
                </Button>
              )}
              <Button onClick={() => { void (async () => {
                if (await confirmDialog({
                  title: subs.length ? "Удалить задачу вместе с подзадачами?" : "Удалить задачу?",
                  text: subs.length ? `Внутри ${subs.length} ${subs.length === 1 ? "подзадача" : "подзадач"} — они удалятся тоже. Действие необратимо.` : "Действие необратимо.",
                  confirmText: "Удалить", danger: true,
                })) onDelete();
              })(); }}>
                <IconTrash size={12} /> Удалить
              </Button>
            </div>
          )}
        </div>

    </>
  );
  if (inline) {
    return (
      <div {...dropHandlers} className="relative flex max-h-[68dvh] w-full flex-col overflow-hidden rounded-[var(--radius-lg)] border border-[var(--border)] bg-[var(--surface)] shadow-[0_4px_24px_rgba(0,0,0,0.18)]">
        {fileInput}
        {dropZone}
        {body}
      </div>
    );
  }
  // ⚠️ ПАНЕЛЬ СПРАВА ВО ВСЮ ВЫСОТУ, а не поповер у карточки (Максим,
  // 01.09.2026: «надо сделать в точности как у них»). Разбор карточки Weeek
  // 01.09.2026: задача открывается панелью примерно на 45% ширины, список
  // задач слева остаётся виден, адрес меняется на ссылку с номером задачи —
  // её можно переслать, и она откроется прямо в этом виде.
  // Прежнее позиционирование «рядом с карточкой» (`besidePos`) больше не
  // используется: панель всегда у правого края.
  return createPortal(
    <>
      {fileInput}
      <div className="fixed inset-0 bg-black/35" style={{ zIndex: Z.MODAL }} onClick={onClose} />
      <div ref={panelRef} {...dropHandlers}
        // Ширина снята с их окна (Максим прислал сравнение 01.09.2026: «размер такой
        // у них»): панель занимает примерно четверть экрана, а не половину.
        className="yuc-slide-right fixed inset-y-0 right-0 flex w-[min(520px,34vw)] max-w-[96vw] flex-col overflow-hidden border-l border-[var(--border)] bg-[var(--surface)] shadow-[0_8px_40px_rgba(0,0,0,0.35)]"
        style={{ zIndex: Z.MODAL + 1 }}
        onClick={(e) => e.stopPropagation()}>
        {dropZone}
        {body}
      </div>
    </>,
    document.body,
  );
}

// Панель заявки на доработку: просмотр (не конвертирует) + действия
// «В задачи» (создать задачу из заявки) / «Отклонить».
/** Панель заявки на доработку. ⚠️ ЭКСПОРТИРУЕТСЯ: её же открывает кабинет —
 *  виджет заявок переехал туда, и открывать карточку нужно на месте, а не
 *  уводить человека в раздел IT (Максим, 04.09.2026). */
export function ReqDetailPanel({ req, anchor, canEdit, onClose, onConvert, onReject }: {
  req: DevRequest; anchor: DOMRect | null; canEdit: boolean; onClose: () => void; onConvert: () => void; onReject: () => void;
}) {
  const [r, setR] = useState<DevRequest>(req);
  const panelRef = useRef<HTMLDivElement>(null);
  const [pos, setPos] = useState<CSSProperties>(() => besidePos(anchor, 400));
  // Позиционирование рядом с карточкой (как у TaskDetailPanel): сдвиг вверх,
  // если не влезает снизу; maxHeight по высоте окна (императивно — см. ниже).
  useLayoutEffect(() => {
    const el = panelRef.current; if (!el) return;
    el.style.maxHeight = "none";
    const W = 400, vw = window.innerWidth, vh = window.innerHeight, H = el.offsetHeight;
    let left: number, top: number;
    if (anchor) {
      left = anchor.right + 8;
      if (left + W > vw - 8) left = anchor.left - W - 8;
      if (left < 8) left = Math.min(Math.max(8, anchor.left), vw - W - 8);
      top = anchor.top;
      if (top + H > vh - 8) top = vh - 8 - H;
      if (top < 8) top = 8;
    } else { left = (vw - W) / 2; top = Math.max(8, (vh - H) / 2); }
    const maxH = vh - top - 12;
    el.style.maxHeight = `${maxH}px`;
    // z выше поповера «Заявки по отделам» (10060), чтобы карточка была поверх списка.
    setPos({ position: "fixed", left, top, width: W, maxHeight: maxH, zIndex: Z.DROPDOWN });
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [anchor, req.id]);
  async function setPriority(priority: DevRequestPriority) {
    try { const u = await api.patchDevRequest(r.id, { priority }); setR(u); } catch { /* тихо */ }
  }
  const inputCls = INPUT_CLS;
  return createPortal(
    <>
      <div className="fixed inset-0" style={{ zIndex: Z.MODAL }} onClick={onClose} />
      <div ref={panelRef} data-req-panel
        className="flex max-w-[94vw] flex-col overflow-hidden rounded-[var(--radius-lg)] border border-[var(--border)] bg-[var(--surface)] shadow-[0_8px_40px_rgba(0,0,0,0.3)]"
        style={pos}
        onClick={(e) => e.stopPropagation()}>
        <div className="border-b border-[var(--border)] p-4">
          <div className="mb-2 flex items-start justify-between gap-2">
            <Button iconOnly onClick={onClose}><IconClose size={16} /></Button>
            <div className="flex items-center gap-2">
              <div className="text-right leading-tight">
                <div className="text-[12px] font-semibold text-[var(--text)]">{r.author_name || "—"}</div>
                <div className="text-[11px] text-[var(--textMuted)] tabular-nums">{fmtDateTime(r.created_at)}</div>
              </div>
              <AuthorAvatar name={r.author_name} />
            </div>
          </div>
          <div className="text-[16px] font-bold leading-snug text-[var(--text)]" >{r.title}</div>
        </div>
        <div className="min-h-0 flex-1 overflow-y-auto scrollbar-autohide p-4">
          <div className="mb-3 flex flex-wrap items-center gap-2 text-[11px]">
            <span className="rounded-[var(--radius-md)] px-2 py-0.5 font-semibold" style={{ color: REQ_COLOR, background: `color-mix(in srgb, ${REQ_COLOR} 14%, transparent)` }}>Заявка на доработку</span>
            {r.tab_label && <span className="text-[var(--textMuted)]">{r.tab_label}</span>}
          </div>
          <label className="mb-3 flex w-40 flex-col gap-0.5 text-[11px] text-[var(--textMuted)]">Приоритет
            <Select
              value={r.priority}
              onChange={(v) => void setPriority(v as DevRequestPriority)}
              disabled={!canEdit}
              size="sm"
              className="w-full"
              options={PRIO_OPTIONS}
            />
          </label>
          <div className="field-label mb-1">Описание</div>
          <div className="whitespace-pre-wrap text-[12px] text-[var(--text)]">{r.description || "—"}</div>

          {canEdit && (
            <div className="mt-5 flex items-center gap-2 border-t border-[var(--border)] pt-3">
              <Button variant="primary" className="flex-1 justify-center" onClick={onConvert}>
                <ArrowRight size={13} /> В задачи
              </Button>
              <Button onClick={onReject}>
                Отклонить
              </Button>
            </div>
          )}
        </div>
      </div>
    </>,
    document.body,
  );
}

export function ItTasksPage({ embedded, canEdit, only }: Props = {}) {
  const { user } = useAuth();
  const edit = canEdit ?? hasPermission(user, "tasks", "edit");

  const [tasks, setTasks] = useState<TeamTask[]>([]);
  // Справочник отделов — только ради цвета чипа категории (тот же источник, что везде).
  const [depts, setDepts] = useState<Department[]>([]);
  useEffect(() => { void api.getDepartments().then(setDepts).catch(() => {}); }, []);
  const [loading, setLoading] = useState(true);
  const [drag, setDrag] = useState<number | null>(null);   // id перетаскиваемой задачи
  const [overCol, setOverCol] = useState<string | null>(null);
  const [openTask, setOpenTask] = useState<TeamTask | null>(null);  // открытая панель задачи
  const [taskAnchor, setTaskAnchor] = useState<DOMRect | null>(null);  // якорь поповера (рядом с карточкой)
  const [openReq, setOpenReq] = useState<DevRequest | null>(null);  // открытая панель заявки
  const [reqAnchor, setReqAnchor] = useState<DOMRect | null>(null);  // якорь поповера заявки
  const [reqReload, setReqReload] = useState(0);  // сигнал перезагрузки виджета «Заявки по отделам»
  const openTaskAt = (tk: TeamTask, el: HTMLElement | null) => { setTaskAnchor(el ? el.getBoundingClientRect() : null); setOpenTask(tk); };
  const openReqAt = (rq: DevRequest, el: HTMLElement | null) => { setReqAnchor(el ? el.getBoundingClientRect() : null); setOpenReq(rq); };
  const [expandedCards, setExpandedCards] = useState<Set<number>>(new Set());  // раскрытые подзадачи на доске
  const [movingTask, setMovingTask] = useState<TeamTask | null>(null);  // режим «выбрать родителя на доске»
  async function moveAsParent(parent: TeamTask) {
    if (!movingTask) return;
    try { await api.patchTask(movingTask.id, { parent_task_id: parent.id }); setMovingTask(null); await load(); } catch { /* тихо */ }
  }
  // Удаление задачи: сначала её подзадачи (дети), потом саму задачу.
  async function deleteTask(t: TeamTask) {
    try {
      for (const c of tasks.filter((x) => x.parent_task_id === t.id)) await api.deleteTask(c.id);
      await api.deleteTask(t.id);
      setOpenTask(null); await load();
    } catch { void load(); }
  }
  // Архив вместо удаления (созвон 03.07): задача уходит с доски, но остаётся в БД.
  const [showArchive, setShowArchive] = useState(false);
  const [archived, setArchived] = useState<TeamTask[]>([]);
  const [arcQ, setArcQ] = useState("");
  // ⚠️ БЫСТРОЕ ДОБАВЛЕНИЕ ПРЯМО В КОЛОНКЕ — механика Weeek (разбор 01.09.2026,
  // просьба Максима «посмотри, как у них добавляются задачи, механика удобна»).
  // У них это не кнопка, открывающая форму, а ПОЛЕ ВВОДА в колонке: печатаешь
  // название, Enter — задача создана в этом статусе, поле остаётся для
  // следующей. Под полем — компактный ряд: приоритет и категория.
  const [addCol, setAddCol] = useState<string | null>(null);   // в какой колонке открыт ввод
  const [addTitle, setAddTitle] = useState("");
  const [addPrio, setAddPrio] = useState<DevRequestPriority>("normal");
  const [addCat, setAddCat] = useState("");
  const [adding, setAdding] = useState(false);
  // ⚠️ ПУСТАЯ ФОРМА ДОБАВЛЕНИЯ ЗАКРЫВАЕТСЯ ПО КЛИКУ МИМО НЕЁ, а не по уходу
  // фокуса из поля. На `onBlur` полагаться нельзя: нажатие на «приоритет» или
  // «отдел» уводит фокус, а `relatedTarget` при этом ПУСТ (список рисуется в
  // портале и забирает указатель до перевода фокуса) — форма схлопывалась
  // раньше, чем человек успевал выбрать значение (Максим, 02.09.2026).
  // Список выпадающего живёт в портале, поэтому исключаем и его обёртку.
  useEffect(() => {
    if (addCol == null) return;
    const onDown = (e: PointerEvent) => {
      const t = e.target as HTMLElement | null;
      if (t?.closest("[data-add-form]") || t?.closest("[data-radix-popper-content-wrapper]")) return;
      if (!addTitle.trim()) { setAddCol(null); setAddTitle(""); }
    };
    document.addEventListener("pointerdown", onDown, true);
    return () => document.removeEventListener("pointerdown", onDown, true);
  }, [addCol, addTitle]);
  // ⚠️ ОТКРЫТИЕ ЗАДАЧИ ПО ССЫЛКЕ: `?task=71`. Ссылку даёт кнопка в шапке
  // карточки — у Weeek это работает так же (адрес меняется при открытии, и
  // ссылку можно переслать). Параметр применяется один раз: дальше человек
  // ходит по доске сам.
  const search = useSearchParams();
  const wantTask = search.get("task");
  const openedFromUrl = useRef(false);
  useEffect(() => {
    if (openedFromUrl.current || !wantTask || !tasks.length) return;
    const найдена = tasks.find((x) => String(x.id) === wantTask);
    if (найдена) { openedFromUrl.current = true; setTaskAnchor(null); setOpenTask(найдена); }
  }, [wantTask, tasks]);
  const loadArchived = useCallback(async () => {
    try { setArchived(await api.listTasks("inbox", true)); } catch { /* тихо */ }
  }, []);
  useEffect(() => { if (showArchive) void loadArchived(); }, [showArchive, loadArchived]);
  async function archiveTask(t: TeamTask, to: boolean) {
    try {
      await api.patchTask(t.id, { archived: to });
      setOpenTask(null);
      await load();
      if (showArchive || to) await loadArchived();
    } catch { void load(); }
  }

  const load = useCallback(async (initial = false) => {
    if (initial) setLoading(true);
    try {
      setTasks(await api.listTasks("inbox"));
    } catch { /* тихо */ } finally { if (initial) setLoading(false); }
  }, []);
  useEffect(() => { void load(true); }, [load]);

  // Автополлинг: подхватывает задачи, поставленные через FAB, без ручного обновления.
  // Не дёргаем во время перетаскивания (dragRef) и когда вкладка не на экране.
  const dragRef = useRef(false);
  useEffect(() => { dragRef.current = drag != null; }, [drag]);
  useEffect(() => {
    const tick = () => { if (!dragRef.current && !document.hidden) void load(); };
    const id = window.setInterval(tick, 20000);
    window.addEventListener("focus", tick);
    return () => { window.clearInterval(id); window.removeEventListener("focus", tick); };
  }, [load]);

  const byStatus = useMemo(() => {
    const m: Record<string, TeamTask[]> = {};
    // Только верхнеуровневые задачи; подзадачи (дети) — внутри панели родителя.
    for (const t of tasks) if (!t.parent_task_id) (m[t.status] ??= []).push(t);
    return m;
  }, [tasks]);

  async function moveTask(id: number, status: TeamTaskStatus) {
    const cur = tasks.find((t) => t.id === id);
    if (!cur || cur.status === status) return;
    setTasks((prev) => prev.map((t) => (t.id === id ? { ...t, status } : t)));  // оптимистично
    try { await api.patchTask(id, { status }); } catch { void load(); }
  }

  // Тач-перетаскивание карточек (планшет/телефон) — long-press-drag на Pointer
  // Events. Мышь остаётся на штатном HTML5-DnD (draggable/onDrag* ниже).
  // При режиме «выбрать родителя» (movingTask) drag выключен, как и HTML5.
  const onCardTouchDown = useKanbanTouchDrag<number>({
    enabled: edit && !movingTask,
    onStart: (id) => setDrag(id),
    onOverColumn: (col) => setOverCol(col),
    onDrop: (col, id) => { void moveTask(id, col as TeamTaskStatus); setDrag(null); setOverCol(null); },
    onEnd: () => { setDrag(null); setOverCol(null); },
  });

  // Клик по заявке = открыть как задачу: создаём TeamTask из заявки, заявку
  // помечаем обработанной (уходит из очереди) и сразу открываем её панель.
  async function convertReq(r: DevRequest) {
    if (!user) return;
    try {
      const task = await api.createTask({ title: r.title, description: r.description, assignee_id: user.id, priority: r.priority });
      await api.patchDevRequest(r.id, { status: "done" });
      await load();
      setReqReload((v) => v + 1);
      setTaskAnchor(null);
      setOpenTask(task);
    } catch { void load(); }
  }
  async function dropReq(r: DevRequest) {
    try { await api.patchDevRequest(r.id, { status: "rejected" }); await load(); setReqReload((v) => v + 1); }
    catch (e) { window.alert(e instanceof Error ? e.message : "Не удалось отклонить заявку"); void load(); }
  }

  const totalTasks = tasks.length;

  // ── Архив: строки и колонки таблицы ──────────────────────────────────────
  const arcRows = useMemo(() => {
    const needle = arcQ.trim().toLowerCase();
    // Подзадачи тоже показываем: раньше их отсекали, и часть архива была не видна.
    return archived.filter(
      (t) => !needle || `${t.title} ${t.author_name} ${t.category ?? ""}`.toLowerCase().includes(needle),
    );
  }, [archived, arcQ]);

  const arcCols: DataCol<TeamTask>[] = useMemo(() => [
    {
      id: "title", label: "Задача", width: "minmax(220px, 2fr)", align: "left",
      sortBy: (t) => t.title,
      cell: (t) => (
        <div className="min-w-0">
          <div className="truncate font-medium text-[var(--text)]">{t.title}</div>
          <div className="truncate text-[11px] text-[var(--textMuted)]">
            {[t.parent_task_id ? "подзадача" : "", t.author_name ? `от ${t.author_name}` : ""].filter(Boolean).join(" · ")}
          </div>
        </div>
      ),
    },
    {
      id: "category", label: "Категория", width: "minmax(120px, 1fr)", align: "left",
      sortBy: (t) => t.category ?? "",
      cell: (t) => (t.category
        ? <span className="inline-block max-w-full truncate rounded-[var(--radius-md)] px-1.5 py-0.5 text-[11px]"
                style={{ background: `color-mix(in srgb, ${deptColorOf(depts, t.category)} 16%, transparent)`, color: deptColorOf(depts, t.category) }}>
            {t.category}
          </span>
        : <span className="text-[var(--textMuted)]">—</span>),
    },
    {
      id: "assignees", label: "Исполнители", width: "minmax(120px, 1fr)", align: "left",
      cell: (t) => (t.assignees?.length
        ? <span className="flex items-center" data-tooltip={t.assignees.map((a) => a.name).join(", ")}>
            {t.assignees.slice(0, 3).map((a, i) => (
              <span key={a.id} className={i ? "-ml-1.5" : ""}>
                <UserAvatar name={a.name} avatarUrl={a.avatar} userId={a.id} size={18} fillBg />
              </span>
            ))}
          </span>
        : <span className="text-[var(--textMuted)]">—</span>),
    },
    {
      id: "prio", label: "Приоритет", width: "minmax(110px, 0.9fr)", align: "left",
      sortBy: (t) => t.priority,
      cell: (t) => (
        <span className="inline-flex items-center gap-1 whitespace-nowrap" style={{ color: prioColor(t.priority) }}>
          <span className="inline-block size-1.5 rounded-full" style={{ background: prioColor(t.priority) }} />
          {prioLabel(t.priority)}
        </span>
      ),
    },
    {
      id: "archived", label: "Закрыта", width: "minmax(120px, 0.9fr)", align: "left",
      sortBy: (t) => t.archived_at ?? "",
      cell: (t) => (t.archived_at
        ? <span className="tabular-nums text-[var(--textMuted)]">
            {new Date(t.archived_at).toLocaleDateString("ru-RU", { day: "2-digit", month: "short", year: "numeric" })}
          </span>
        : <span className="text-[var(--textMuted)]">—</span>),
    },
  ], [depts]);

  /** Удалить из архива насовсем — со спросом, как просил Максим. */
  async function deleteFromArchive(t: TeamTask) {
    const ok = await confirmDialog({
      title: "Удалить задачу насовсем?",
      text: `«${t.title}» исчезнет из архива без возможности вернуть. Если нужна история — оставьте её в архиве.`,
      confirmText: "Удалить",
      danger: true,
    });
    if (!ok) return;
    try { await api.deleteTask(t.id); await loadArchived(); await load(); } catch { /* тихо */ }
  }

  /** Создать задачу из поля в колонке: статус — этой колонки, исполнитель — я. */
  async function quickAdd(status: string) {
    const title = addTitle.trim();
    if (!title || adding || !user) return;
    setAdding(true);
    try {
      const создана = await api.createTask({
        title, assignee_id: user.id, assignee_ids: [user.id],
        priority: addPrio, category: addCat || undefined,
      });
      // Статус задаём вторым шагом: создание всегда кладёт задачу в «Новые».
      if (status !== "new") await api.patchTask(создана.id, { status });
      setAddTitle("");                       // поле остаётся открытым — как у них
      await load();
    } catch { /* тихо */ } finally { setAdding(false); }
  }

  const TaskCard = (t: TeamTask, idx = 0) => {
    const accent = prioColor(t.priority) ?? "#64748b";
    const period = fmtPeriod(t.start_date, t.due_date);
    const sub = [t.author_name ? `от ${t.author_name}` : "", t.due_date ? `до ${fmtDate(t.due_date)}` : ""].filter(Boolean).join(" · ");
    const children = tasks.filter((x) => x.parent_task_id === t.id);
    const childDone = children.filter((c) => c.status === "done").length;
    const expanded = expandedCards.has(t.id);
    const eligibleTarget = movingTask && movingTask.id !== t.id && t.parent_task_id !== movingTask.id;
    const isMovingSelf = movingTask?.id === t.id;
    return (
      <div key={t.id} className="flex flex-col gap-1">
        <div
          draggable={edit && !movingTask}
          onDragStart={() => setDrag(t.id)}
          onDragEnd={() => { setDrag(null); setOverCol(null); }}
          onPointerDown={(e) => onCardTouchDown(e, t.id)}
          onClick={(e) => { if (movingTask) { if (eligibleTarget) void moveAsParent(t); } else openTaskAt(t, e.currentTarget); }}
          // ⚠️ БЕЗ ЦВЕТНОЙ ОБВОДКИ. Раньше карточка была обведена рамкой цвета
          // приоритета (`outline-cell`) — на доске это давало пять разноцветных
          // контуров в ряд. В образце заказчика контура нет вовсе: слева тонкая
          // полоска, остальное держится тоном подложки
          // (Максим, 01.09.2026: «глянь разницу в цветах и толщине линий»).
          className={`kanban-card glass-flat relative flex cursor-pointer items-start gap-1.5 overflow-hidden rounded-[var(--radius-md)] py-2 pl-3 pr-2.5 text-left ${edit && !movingTask ? "active:cursor-grabbing" : ""} ${drag === t.id ? "is-dragging" : ""} ${eligibleTarget ? "ring-2 ring-[var(--primary)]" : ""} ${isMovingSelf ? "opacity-40" : ""}`}
          style={{ ["--gc"]: accent, background: "var(--surface)", animationDelay: `${Math.min(idx, 6) * 22}ms` } as CSSProperties}
          // Подсказки «Открыть детали» нет: карточку и так открывают кликом
          // (Максим, 01.09.2026 — «она только мешается»). Осталась только
          // подсказка режима переноса: там неочевидно, куда можно бросить.
          data-tooltip={movingTask && eligibleTarget ? "Перенести сюда" : undefined}>
          {/* ⚠️ ЦВЕТ ПОЛОСКИ = ПРИОРИТЕТ (решение Максима 01.09.2026). Был цвет
              статуса — но статус и так написан в шапке колонки, над которой
              карточка лежит, и полоска повторяла уже известное. Приоритет же
              иначе виден только подписью в нижнем углу: полоска даёт его с
              одного взгляда на всю колонку. `accent` = prioColor(t.priority). */}
          {t.priority && (
            <span className="absolute inset-y-0 left-0 w-[2px]"
                  style={{ background: `color-mix(in srgb, ${accent} 78%, transparent)` }} />
          )}
          {/* ⚠️ `glass-flat` — НЕ оформление, а отказ от общей тени кнопок:
              в тёмной теме глобальное правило вешает на любую <button>
              `inset 0 1px 0` + `0 2px 8px rgba(0,0,0,.22)`, и у голого значка
              это читалось как грязный ореол (Максим, 02.09.2026). */}
          {children.length > 0 && (
            <button type="button"
              onClick={(e) => { e.stopPropagation(); setExpandedCards((p) => { const n = new Set(p); if (n.has(t.id)) n.delete(t.id); else n.add(t.id); return n; }); }}
              className="glass-flat -ml-0.5 shrink-0 text-[var(--textMuted)] hover:text-[var(--primary)]"
              data-tooltip={expanded ? "Свернуть подзадачи" : "Показать подзадачи"}>
              {expanded ? <IconChevronDown size={16} /> : <IconChevronRight size={16} />}
            </button>
          )}
          {/* ⚠️ СОСТАВ КАРТОЧКИ — ПО ОБРАЗЦУ заказчика (photo_2026-08-25_22-44-28):
              заголовок, чип категории своим цветом, ниже ОДИН ряд — лица
              исполнителей, срок, приоритет точкой. Раньше здесь была строка
              «от кого · до когда» текстом: она не отвечала на вопрос «кто
              делает», а лица отвечают сразу. */}
          <span className="flex min-w-0 flex-1 flex-col gap-1">
            <span className="line-clamp-2 text-[12px] font-semibold leading-snug text-[var(--text)]">{t.title}</span>
            {t.category && (
              <span className="inline-block max-w-full truncate self-start rounded-[var(--radius-md)] px-1.5 py-0.5 text-[11px]"
                    style={{
                      background: `color-mix(in srgb, ${deptColorOf(depts, t.category)} 16%, transparent)`,
                      color: deptColorOf(depts, t.category),
                    }}>
                {t.category}
              </span>
            )}
            <span className="flex items-center gap-2 text-[11px]">
              {/* ⚠️ СКРЕПКА СО СЧЁТЧИКОМ: по карточке видно, что к задаче
                  приложены файлы, — как у них на доске. Нажатие открывает
                  задачу (карточка и так кликается), файлы там первой секцией. */}
              {t.attachments?.length > 0 && (
                <span className="inline-flex shrink-0 items-center gap-0.5 text-[var(--textMuted)]"
                      data-tooltip={`Вложений: ${t.attachments.length}`}>
                  <IconAttach size={11} />
                  <span className="tabular-nums">{t.attachments.length}</span>
                </span>
              )}
              {t.assignees?.length > 0 && (
                <span className="flex shrink-0 items-center" data-tooltip={t.assignees.map((a) => a.name).join(", ")}>
                  {t.assignees.slice(0, 3).map((a, i) => (
                    <span key={a.id} className={i ? "-ml-1.5" : ""}>
                      <UserAvatar name={a.name} avatarUrl={a.avatar} userId={a.id} size={16} fillBg />
                    </span>
                  ))}
                </span>
              )}
              {period && <span className="truncate text-[var(--textMuted)]">{period}</span>}
              {/* Приоритета может не быть — тогда в углу пусто, а не серая
                  надпись «без приоритета»: пустое место читается быстрее. */}
              {t.priority && (
                <span className="ml-auto inline-flex shrink-0 items-center gap-1 whitespace-nowrap" style={{ color: accent }}>
                  <span className="inline-block size-1.5 rounded-full" style={{ background: accent }} />
                  {prioLabel(t.priority)}
                </span>
              )}
            </span>

            {/* ⚠️ В СВЁРНУТОЙ КАРТОЧКЕ ПОДЗАДАЧ НЕ ВИДНО — только полоса
            выполнения со счётчиком «3/4» (Максим, 02.09.2026). 01.09 список
            стоял прямо на карточке: две задачи с подзадачами растягивали
            колонку на 600 px, и доска не читалась. Сам список — по шеврону. */}
        {children.length > 0 && !expanded && (
          <div className="mt-2 flex flex-col gap-0.5 pl-3.5">
            {/* ⚠️ Полоса ТОНКАЯ и одного цвета на всех карточках (Максим,
                01.09.2026: «у них аккуратнее, линия не такая толстая»). Замер их
                карточки: высота 2px, цвет #ffa12f — один для всех, НЕ по
                приоритету. У нас было 3px цветом приоритета, и у задачи с
                «Высоким» полоса горела красным, будто это тревога. Слева —
                значок подзадач, как у них. */}
            <div className="flex items-center gap-1.5">
              <ListChecks size={11} className="shrink-0 text-[var(--textMuted)]" />
              <div className="h-[2px] flex-1 overflow-hidden rounded-full" style={{ background: "color-mix(in srgb, var(--textMuted) 22%, transparent)" }}>
                <div className="h-full rounded-full transition-[width]"
                     style={{ width: `${Math.round((childDone / children.length) * 100)}%`, background: "var(--warning)" }} />
              </div>
              <span className="shrink-0 text-[11px] tabular-nums text-[var(--textMuted)]">{childDone}/{children.length}</span>
            </div>
          </div>
        )}
          </span>
        </div>
        {expanded && children.length > 0 && (
          // Интервал между пунктами 2 px, а не 6 (Максим, 02.09.2026: «поплотнее»).
          // Строка остаётся 24 px — ниже 22 в неё не попасть пальцем.
          <div className="ml-3 flex flex-col gap-0.5 pl-1">
            {children.map((c) => {
              const cd = c.status === "done";
              return (
                // ⚠️ БЕЗ ОБВОДКИ И СВОЕЙ ПОДЛОЖКИ (Максим, 02.09.2026: «обводки
                // подзадач только место занимают»). Было: рамка цвета приоритета
                // (`outline-cell`), фон `var(--card)`, скругление 12px и подъём на
                // пиксель под курсором — пять таких рамок под карточкой читались
                // как пять отдельных карточек. Теперь строка, как в таблицах
                // портала: отклик — подложка, ничего не всплывает.
                <div key={c.id} role="button" tabIndex={0}
                  onClick={(e) => openTaskAt(c, e.currentTarget)}
                  // Отклик на курсор — ТОТ ЖЕ, что у карточки задачи, и живёт он
                  // в globals.css (`.kanban-subrow:hover`), а НЕ классом
                  // `hover:bg-…`: такие классы Tailwind прячет в
                  // @media (hover: hover), и на мыши по удалённому столу они
                  // мертвы (Максим, 02.09.2026 — «не выделяет»).
                  // Пробел — такая же клавиша открытия, как Enter (иначе строка
                  // объявлена кнопкой, а по-кнопочному не работает). Проверка
                  // target===currentTarget обязательна: Enter на кружке отметки
                  // всплывал сюда и заодно открывал задачу.
                  onKeyDown={(e) => {
                    if (e.target !== e.currentTarget) return;
                    if (e.key === "Enter" || e.key === " ") { e.preventDefault(); openTaskAt(c, e.currentTarget); }
                  }}
                  className="kanban-subrow flex cursor-pointer items-center gap-2 rounded-[var(--radius-md)] px-1.5 py-1 text-left text-[12px] transition">
                  {/* Кружок РАБОЧИЙ: подзадача отмечается прямо на доске. В
                      свёрнутой карточке списка больше нет, и без этого отметить
                      её можно было бы только внутри задачи. */}
                  <button type="button" disabled={!edit} aria-label={cd ? "Снять отметку" : "Отметить выполненной"}
                    onClick={(e) => { e.stopPropagation(); void api.patchTask(c.id, { status: cd ? "new" : "done" }).then(() => load()).catch(() => {}); }}
                    // ⚠️ Размер ЯВНЫЙ: у портала своя шкала отступов, и `size-4`
                    // даёт не 16 px, а 12 — кружок выходил меньше, чем в образце,
                    // и в него было трудно попасть.
                    className="glass-flat grid h-[15px] w-[15px] shrink-0 place-items-center rounded-full border transition"
                    style={{
                      borderColor: cd ? "color-mix(in srgb, var(--success) 70%, transparent)" : "color-mix(in srgb, var(--text) 22%, transparent)",
                      background: cd ? "color-mix(in srgb, var(--success) 62%, var(--surface))" : "transparent",
                    }}>
                    {cd && <IconCheck size={9} className="text-white" />}
                  </button>
                  <span className={`min-w-0 flex-1 truncate font-normal ${cd ? "text-[var(--textMuted)] line-through" : "text-[var(--text)]"}`}>{c.title}</span>
                  {c.due_date && <span className="shrink-0 text-[11px] tabular-nums text-[var(--textMuted)]" data-tooltip="Срок">до {fmtDate(c.due_date)}</span>}
                </div>
              );
            })}
          </div>
        )}
      </div>
    );
  };

  if (loading) {
    return <div className="flex h-40 items-center justify-center text-[var(--textMuted)]"><IconSpinner className="animate-spin" size={20} /></div>;
  }

  const board = (
    <div className={`glass-panel rounded-[var(--radius-lg)] border border-[var(--border)] p-4 ${embedded ? "flex h-full min-h-0 flex-col" : ""}`}>
      <div className="mb-3 flex items-center justify-between">
        <span className="section-title" >Доска задач</span>
        <button type="button" onClick={() => setShowArchive((v) => !v)}
          data-tooltip="Закрытые задачи не удаляются, а уходят в архив"
          className={`inline-flex items-center gap-1.5 rounded-[var(--radius-md)] border px-2.5 py-1 text-[11px] font-semibold transition ${showArchive ? "seg seg-on" : "seg"}`} style={{ boxShadow: "none" }}>
          <Archive size={12} /> Архив
        </button>
      </div>
      {movingTask && (
        <div className="mb-3 flex items-center gap-2 rounded-[var(--radius-md)] border border-[var(--primary)] bg-[color-mix(in_srgb,var(--primary)_10%,transparent)] px-3 py-1.5 text-[11px]">
          <span className="text-[var(--text)]">Выберите задачу, в подзадачи которой перенести «{movingTask.title}»</span>
          <span className="flex-1" />
          <button type="button" onClick={() => setMovingTask(null)} className="shrink-0 font-semibold text-[var(--textMuted)] hover:text-[var(--text)]">Отмена</button>
        </div>
      )}

      {/* ⚠️ ВЫСОТА ДОСКИ ПОСТОЯННА (Максим, 02.09.2026: «прыжки лишние»).
          Встроенная в кабинет доска занимает отведённую высоту целиком и
          листается ЦЕЛИКОМ — одной прокруткой на все колонки. Раскрытие
          карточки меняет только содержимое внутри этой области: ни доска, ни
          блоки под ней не двигаются. Колонки при этом остаются по своему
          содержимому (`items-start`) — пустая не тянется до низа. */}
      <div className={`-mx-4 flex gap-3 overflow-x-auto px-4 pb-1 scrollbar-autohide ${embedded ? "min-h-0 flex-1 items-stretch" : "items-start"}`}>
        {/* Столбцы-этапы задач (Новые / В работе / Готово / Бэклог).
            Заявки на доработку — не здесь, они в виджете «Заявки по отделам». */}
        {STAGES.map((st) => {
          const items = byStatus[st.key] ?? [];
          const isOver = overCol === st.key;
          return (
            <div key={st.key}
              data-kanban-col={st.key}
              onDragOver={edit && drag != null ? (e) => { e.preventDefault(); setOverCol(st.key); } : undefined}
              onDragLeave={edit && drag != null ? () => setOverCol((c) => (c === st.key ? null : c)) : undefined}
              onDrop={edit && drag != null ? () => { void moveTask(drag, st.key); setDrag(null); setOverCol(null); } : undefined}
              // ⚠️ У колонки СВОЯ подложка (образец: фон → колонка → карточка,
              // каждый слой светлее предыдущего на четыре единицы).
              style={{ background: isOver ? undefined : "color-mix(in srgb, var(--surface) 80%, var(--bg))" }}
              className={`kanban-col flex min-w-[248px] flex-1 flex-col rounded-[var(--radius-md)] p-2 ${embedded ? "min-h-0" : ""} ${isOver ? "is-drop-target ring-2 ring-[var(--primary)] bg-[color-mix(in_srgb,var(--primary)_6%,transparent)]" : ""}`}>
              <ColumnHeader label={st.label} color={st.color} count={items.length} />
              <div className={`flex flex-col gap-2 pt-1 ${embedded ? "kanban-col-scroll min-h-0 flex-1" : ""}`}>
                {items.map((t, i) => TaskCard(t, i))}
                {/* Плашка видна ТОЛЬКО пока тащат карточку: в покое пустая
                    колонка — это просто пустая колонка, как в образце. */}
                {items.length === 0 && drag != null && (
                  <div className={`kanban-drop-hint rounded-[var(--radius-lg)] border border-dashed border-[var(--border)] py-6 text-center text-[11px] text-[var(--textMuted)] transition-colors ${isOver ? "is-active" : ""}`}>
                    {isOver ? "Отпустите здесь" : "Пусто"}
                  </div>
                )}
              </div>
              {/* ⚠️ «Добавить задачу» — ПОД списком, как в образце: колонка
                  читается сверху вниз, и кнопка не отодвигает первую карточку
                  от заголовка. */}
              {edit && (
                addCol === st.key ? (
                  <div data-add-form className="mt-2 rounded-[var(--radius-md)] border border-[var(--primary)] bg-[var(--surface)] p-2">
                    <textarea
                      autoFocus rows={2} value={addTitle} maxLength={250}
                      onChange={(e) => setAddTitle(e.target.value)}
                      onKeyDown={(e) => {
                        if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); void quickAdd(st.key); }
                        if (e.key === "Escape") { setAddCol(null); setAddTitle(""); }
                      }}
                      placeholder="Добавить задачу"
                      className="w-full resize-none bg-transparent text-[12px] leading-snug text-[var(--text)] outline-none placeholder:text-[var(--textMuted)]"
                    />
                    {/* ⚠️ ОДНОЙ СТРОКОЙ ЭТО НЕ ВЛЕЗАЕТ: колонка 248 px, а счётчик
                        со списками просят 266 — правый список обрезался краем
                        колонки (Максим, 02.09.2026). Поэтому списки идут своей
                        строкой и делят ширину поровну, счётчик — над ними
                        справа. */}
                    <div className="mt-1 flex items-center gap-1.5">
                      <span className="flex-1" />
                      <span className="text-[11px] tabular-nums text-[var(--textMuted)]">{250 - addTitle.length}</span>
                    </div>
                    <div className="mt-1 flex items-center gap-1.5">
                      {/* ⚠️ Приоритет и категория — СТАНДАРТНЫМ полем портала, без
                          правок по месту. Здесь стояло `border-0 bg-[var(--secondary)]
                          py-0`: две бесформенные пилюли на чужой подложке, приплюснутые
                          по высоте (Максим, 02.09.2026 — «не вписываются в стиль»).
                          Тот же урок, что с карточкой задачи 01.09: берём готовый
                          элемент как есть, ширину задаём содержимым. */}
                      <Select value={addPrio} onChange={(v) => setAddPrio(v as DevRequestPriority)}
                        options={PRIO_OPTIONS} size="xs" className="min-w-0 flex-1" />
                      <Select value={addCat} onChange={setAddCat}
                        options={[{ value: "", label: "отдел" }, ...depts.filter((d) => d.key !== "system").map((d) => ({ value: d.title, label: d.title }))]}
                        size="xs" className="min-w-0 flex-1" />
                    </div>
                  </div>
                ) : (
                  <button type="button" onClick={(e) => {
                      setAddCol(st.key); setAddTitle("");
                      // Список колонки прокручиваем вниз: форма стоит под ним, и
                      // при длинной колонке открывалась бы за нижним краем.
                      const список = e.currentTarget.parentElement?.querySelector(".kanban-col-scroll");
                      requestAnimationFrame(() => список?.scrollTo({ top: список.scrollHeight, behavior: "smooth" }));
                    }}
                    className="glass-hover mt-2 w-full rounded-[var(--radius-md)] border border-dashed border-[var(--border)] py-2 text-[11px] text-[var(--textMuted)]">
                    Добавить задачу
                  </button>
                )
              )}
            </div>
          );
        })}
      </div>

    </div>
  );

  // Ссылок на соседние модули здесь НЕТ: модули «IT-проекты» и «Дашборд IT»
  // удалены из портала (28.07, просьба Олега).
  return (
    // ⚠️ ВСТРОЕННАЯ ДОСКА БЕЗ СВОИХ ПОЛЕЙ: снаружи их уже дал кабинет, и доска
    // получала отступ дважды — панель выходила уже соседних блоков на 12 px с
    // каждой стороны (Максим, 02.09.2026). Отдельной страницей поля нужны,
    // поэтому в не-embedded они остаются.
    <div className={`tab-top ${embedded ? "flex h-full min-h-0 flex-col" : "px-4 pb-4"}`}>
      <TabHeader
        className="mb-4"
        // Внутри отдела заголовок не печатаем: название раздела и ряд вкладок
        // уже стоят выше (Максим, 27.08.2026).
        title={embedded ? undefined : only === "widgets" ? "IT" : "Задачи"}
        subtitle={embedded ? undefined : only === "widgets"
          ? "Заявки на доработку, переезд отделов на портал, календарь и база знаний"
          : "Личные задачи: колонки по статусу"}
      />
      <div className={embedded ? "flex min-h-0 flex-1 flex-col" : ""}>
        {only !== "widgets" && <div className={embedded ? "min-h-0 flex-1" : "mb-4"}>{board}</div>}
        {only !== "board" && (
          <div className="flex shrink-0 flex-col gap-4 lg:flex-row lg:items-stretch">
            <div className="lg:w-[420px] lg:shrink-0">
              <ItprCalendarWidget canSettings={hasPermission(user, "tasks", "settings")}
                onOpenTask={(id, rect) => { const tk = tasks.find((x) => x.id === id); if (tk) { setTaskAnchor(rect); setOpenTask(tk); } }} />
            </div>
            <div className="min-w-0 flex-1">
              <ItprDeptMigrationWidget canEdit={edit} />
            </div>
            <div className="flex min-w-0 flex-1 flex-col gap-4">
              <ItprDeptRequestsWidget onOpenReq={openReqAt} reloadKey={reqReload} />
              <KnowledgeBaseWidget />
            </div>
          </div>
        )}
      </div>

      {/* ── Архив ────────────────────────────────────────────────────────
          ⚠️ ПАНЕЛЬ, А НЕ БЛОК ПОД ДОСКОЙ (Максим, 01.09.2026: «подумай, как
          сделать архив более удобным»). Было: ряд плиток под доской, в плитке
          только обрезанное название и значок, который читался как корзина
          («у нас там хуита с буквами» — про соседний экран, но тут та же
          история: непонятно, что делает кнопка). Не видно было ни когда
          закрыли, ни кем, ни из какой колонки; подзадачи в архиве вообще не
          показывались; удалить насовсем было нельзя; со временем ряд плиток
          превратился бы в кашу.
          Стало: таблица на общем `DataTable` в общей же модалке портала —
          сортировка и поиск бесплатно, вид как у остальных таблиц. Действия
          строки подписаны словами, удаление спрашивает подтверждение. */}
      {showArchive && (
        <Modal title="Архив задач" subtitle="Закрытые задачи не удаляются, а уходят сюда" size="xl"
               onClose={() => setShowArchive(false)}>
          <div className="flex flex-col gap-3">
            <div className="relative">
              <Search size={14} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-[var(--textMuted)]" />
              <input value={arcQ} onChange={(e) => setArcQ(e.target.value)}
                placeholder="Поиск по архиву…" className="field-sm field-icon w-full pr-2.5" />
            </div>
            <div className="table-surface rounded-[var(--radius-md)]">
              <DataTable<TeamTask>
                headStyle="bold"
                rows={arcRows}
                columns={arcCols}
                getRowKey={(t) => t.id}
                defaultSortId="archived"
                defaultSortDir="desc"
                emptyTitle={archived.length ? "Ничего не нашлось" : "Архив пуст"}
                emptyText={archived.length ? "Попробуйте изменить запрос." : "Сюда попадают закрытые задачи."}
                rowActionsWidth={230}
                rowActions={(t) => (
                  <>
                    <Button variant="secondary" onClick={() => void archiveTask(t, false)}>Вернуть на доску</Button>
                    <Button variant="danger" onClick={() => void deleteFromArchive(t)}>Удалить</Button>
                  </>
                )}
              />
            </div>
          </div>
        </Modal>
      )}

      {openTask && (
        <TaskDetailPanel key={openTask.id} task={openTask} allTasks={tasks} anchor={taskAnchor} canEdit={edit}
          onClose={() => setOpenTask(null)} onChanged={() => void load()}
          onOpenTask={(child, rect) => { setTaskAnchor(rect); setOpenTask(child); }}
          onPickParent={() => { setMovingTask(openTask); setOpenTask(null); }}
          onDelete={() => void deleteTask(openTask)}
          onArchive={() => void archiveTask(openTask, true)} />
      )}
      {openReq && (
        <ReqDetailPanel req={openReq} anchor={reqAnchor} canEdit={edit}
          onClose={() => setOpenReq(null)}
          onConvert={() => { const r = openReq; setOpenReq(null); void convertReq(r); }}
          onReject={() => { const r = openReq; setOpenReq(null); void dropReq(r); }} />
      )}
    </div>
  );
}
